// Mock data for the Procurement Management System

export interface User {
  id: number;
  username: string;
  password: string; // In real app, this would be hashed
  role: 'admin' | 'approver' | 'quality_inspector' | 'user' | 'procurement_manager';
  name: string;
  email: string;
  department?: string;
  avatar?: string;
  isActive: boolean;
  createdAt: string;
  lastLogin?: string;
}

export interface SKU {
  id: number;
  skuCode: string;
  name: string;
  description: string;
  category: string;
  unit: string;
  currentStock: number;
  reorderLevel: number;
  maxStock: number;
  linkedVendors: VendorLink[];
  priceHistory: PriceHistoryEntry[];
  lastPurchasePrice?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: number;
  specifications?: Record<string, any>;
  image?: string;
}

export interface VendorLink {
  vendorId: number;
  defaultPrice: number;
  leadTime: number; // in days
  minimumOrderQty: number;
  lastUpdated: string;
  isPreferred: boolean;
}

export interface PriceHistoryEntry {
  id: number;
  vendorId: number;
  price: number;
  date: string;
  changedBy: number;
  reason?: string;
  poId?: number;
  quantity?: number;
}

export interface Vendor {
  id: number;
  vendorCode: string;
  name: string;
  contactDetails: {
    email: string;
    phone: string;
    website?: string;
    address: {
      street: string;
      city: string;
      state: string;
      zip: string;
      country: string;
    };
    primaryContact: {
      name: string;
      title: string;
      email: string;
      phone: string;
    };
  };
  paymentTerms: string;
  linkedSKUs: number[];
  performanceRating: number;
  deliveryMetrics: {
    totalOrders: number;
    onTimeDeliveries: number;
    qualityScore: number;
    responseTime: number; // hours
  };
  certifications: string[];
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    routingNumber: string;
  };
  taxInfo: {
    taxId: string;
    gstNumber?: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseOrder {
  id: number;
  poNumber: string;
  vendorId: number;
  items: POItem[];
  totalAmount: number;
  status: 'CREATED' | 'APPROVED' | 'REJECTED' | 'NEEDS_REVISION' | 'PARTLY_RECEIVED' | 'FULLY_RECEIVED' | 'FULLY_REJECTED' | 'PENDING_APPROVAL' | 'OVER_INVOICED';
  priority: 'Low' | 'Medium' | 'High' | 'Urgent';
  deliveryDetails: {
    expectedDate: string;
    actualDate?: string;
    address: string;
    instructions?: string;
  };
  gateEntry?: GateEntry;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  notes?: string;
  attachments?: string[];
  // New fields for IQC and security verification
  securityVerified?: boolean;
  securityVerifiedBy?: number;
  securityVerifiedAt?: string;
  iqcCompleted?: boolean;
  iqcCompletedBy?: number;
  iqcCompletedAt?: string;
  // Approval workflow fields
  needsRevision?: boolean;
  approvalRemarks?: string;
  approvedBy?: number;
  approvedAt?: string;
  rejectedBy?: number;
  rejectedAt?: string;
  approvalHistory?: ApprovalHistoryEntry[];
  // Vendor communication fields
  sentToVendor?: boolean;
  sentToVendorDate?: string;
}

export interface ApprovalHistoryEntry {
  id: number;
  action: 'created' | 'approved' | 'rejected' | 'requested_changes';
  performedBy: number;
  performedAt: string;
  remarks?: string;
}

export interface POItem {
  id: number;
  skuId: number;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  specifications?: Record<string, any>;
  // IQC testing fields
  iqcTested?: boolean;
  iqcTestedBy?: number;
  iqcTestedAt?: string;
  iqcNotes?: string;
}


// RBAC interfaces
export interface Role {
  id: number;
  name: string;
  description: string;
  privileges: string[];
  isActive: boolean;
}

export interface Privilege {
  id: number;
  name: string;
  description: string;
  resource: string;
  action: string;
}

// Approval action interfaces
export interface ApprovalAction {
  id: number;
  poId: number;
  action: 'approve' | 'reject' | 'request_changes';
  approverId: number;
  comments?: string;
  timestamp: string;
  level: number;
}

export interface IQCTestResult {
  id: number;
  poId: number;
  itemId: number;
  testedBy: number;
  testedAt: string;
  acceptedQuantity: number;
  rejectedQuantity: number;
  notes?: string;
  testResults?: Record<string, any>;
}

export interface GateEntry {
  id: number;
  poId: number;
  vehicleNumber: string;
  driverName: string;
  driverPhone: string;
  entryTime: string;
  exitTime?: string;
  securityOfficer: number;
  items: GateEntryItem[];
  photos?: string[];
  notes?: string;
}

export interface GateEntryItem {
  skuId: number;
  expectedQuantity: number;
  receivedQuantity: number;
  condition: 'Good' | 'Damaged' | 'Partial';
  notes?: string;
}

export interface QualityInspection {
  id: number;
  poId: number;
  inspectorId: number;
  inspectionDate: string;
  items: InspectionItem[];
  overallStatus: 'Pending' | 'In Progress' | 'Passed' | 'Failed' | 'Partly Accepted';
  notes?: string;
  photos?: string[];
  certificateRequired: boolean;
  certificateReceived: boolean;
  reportGenerated: boolean;
}

export interface InspectionItem {
  id: number;
  skuId: number;
  receivedQty: number;
  inspectedQty: number;
  acceptedQty: number;
  rejectedQty: number;
  rejectionReasons: string[];
  qualityRating: number;
  testResults?: Record<string, any>;
}

export interface InvoiceItem {
  id: number;
  skuId: number;
  skuCode: string;
  skuName: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  description?: string;
  receivedQuantity?: number; // Quantity actually received
  pendingQuantity?: number; // Quantity still to be received
}

export interface Invoice {
  id: number;
  invoiceNumber: string;
  poId: number;
  poNumber: string;
  vendorId: number;
  vendorName: string;
  poAmount: number; // Total PO amount for tracking
  amount: number; // Invoice amount
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  issueDate: string;
  invoiceDate: string;
  dueDate: string;
  status: 'Draft' | 'Created' | 'Pending' | 'Approved' | 'Rejected' | 'Received' | 'Handed Over' | 'Complete' | 'Fully Accepted' | 'Partly Accepted';
  receivedStatus: 'Not Received' | 'Received' | 'Normal'; // Quantity-based status
  paymentStatus?: 'Paid' | 'Unpaid' | 'Overdue' | 'Partial'; // Payment status
  paymentTerms: string;
  paymentMethod?: string;
  paymentDate?: string;
  paymentReference?: string;
  // Quantity tracking
  poQuantity: number; // Total quantity ordered in PO
  invoicedQuantity: number; // Quantity invoiced in this invoice
  receivedQuantity: number; // Total quantity received so far (across all invoices for this PO)
  pendingQuantity: number; // Quantity still to be received
  items: InvoiceItem[];
  notes?: string;
  attachments: string[];
  createdAt: string;
  updatedAt: string;
  // Quality testing fields
  qualityTested?: boolean;
  qualityTestedBy?: number;
  qualityTestedAt?: string;
  qualityRating?: number;
  qualityNotes?: string;
  qualityImages?: string[];
  // Quantity testing fields
  quantityTested?: boolean;
  quantityTestedBy?: number;
  quantityTestedAt?: string;
  quantityTestResults?: {
    totalPOQuantity: number;
    totalReceivedQuantity: number;
    totalAcceptedQuantity: number;
    totalRejectedQuantity: number;
  };
  // Delivery status fields
  isReceived: boolean;
  receivedAt?: string;
  deliveryStatus: 'Not Received' | 'Received';
  deliveredBy?: number; // User ID who marked as delivered
  deliveryNotes?: string;
  // Manager Section fields
  handedOverAt?: string;
  handedOverBy?: number; // User ID who handed over
  handedOverByName?: string; // Name of person who handed over
  receivedBy?: number; // Manager ID who received
  receivedByName?: string; // Manager name who received
}

export interface HandoverLog {
  id: number;
  handoverId: string;
  invoiceId: number;
  invoiceNumber: string;
  floorManagerName: string;
  floorManagerContact: string;
  handoverDateTime: string;
  storageLocation: string;
  status: 'Handed Over' | 'Completed';
  createdAt: string;
  updatedAt: string;
}

export interface VendorInvoice {
  id: number;
  invoiceNumber: string;
  poId: number;
  vendorId: number;
  vendorName: string;
  poNumber: string;
  items: VendorInvoiceItem[];
  subtotal: number;
  taxAmount: number;
  totalAmount: number;
  status: 'sent' | 'delivered' | 'verified' | 'paid' | 'disputed';
  sentDate: string;
  deliveredDate?: string;
  verifiedDate?: string;
  paidDate?: string;
  dueDate: string;
  notes?: string;
  attachments?: string[];
  createdAt: string;
  updatedAt: string;
}

export interface VendorInvoiceItem {
  id: number;
  skuId: number;
  skuName: string;
  skuCode: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  deliveredQuantity?: number;
  acceptedQuantity?: number;
  rejectedQuantity?: number;
  qualityNotes?: string;
}

export interface DebitNoteItem {
  id: number;
  skuId: number;
  skuName: string;
  skuCode: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  rejectedQuantity: number;
  rejectionReason: string;
}

export interface DebitNote {
  id: number;
  debitNoteNumber: string;
  linkedPOId?: number;
  linkedInvoiceId?: number;
  linkedPONumber?: string;
  linkedInvoiceNumber?: string;
  vendorId: number;
  vendorName: string;
  reason: string;
  items: DebitNoteItem[];
  totalAmount: number;
  status: 'Open' | 'Sent to Vendor' | 'Closed' | 'Disputed';
  createdAt: string;
  createdBy: number;
  createdByName: string;
  sentToVendorAt?: string;
  closedAt?: string;
  notes?: string;
  attachments?: string[];
}

export interface POSentToVendor {
  id: number;
  poId: number;
  vendorId: number;
  sentDate: string;
  sentBy: number;
  deliveryMethod: 'email' | 'portal' | 'fax' | 'mail';
  status: 'sent' | 'acknowledged' | 'in_progress' | 'completed';
  acknowledgmentDate?: string;
  expectedDeliveryDate?: string;
  notes?: string;
  attachments?: string[];
  createdAt: string;
  updatedAt: string;
}

// Mock Users
const mockUsers: User[] = [
  {
    id: 1,
    username: 'admin',
    password: 'admin123',
    role: 'admin',
    name: 'System Administrator',
    email: 'admin@company.com',
    department: 'IT',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-20T08:30:00Z'
  },
  {
    id: 2,
    username: 'approver1',
    password: 'approve123',
    role: 'approver',
    name: 'John Smith',
    email: 'john.smith@company.com',
    department: 'Procurement',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-19T14:20:00Z'
  },
  {
    id: 3,
    username: 'qc_inspector',
    password: 'quality123',
    role: 'quality_inspector',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@company.com',
    department: 'Quality Control',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-20T09:15:00Z'
  },
  {
    id: 4,
    username: 'procurement_mgr',
    password: 'procure123',
    role: 'procurement_manager',
    name: 'Michael Brown',
    email: 'michael.brown@company.com',
    department: 'Procurement',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-20T07:45:00Z'
  },
  {
    id: 5,
    username: 'user1',
    password: 'user123',
    role: 'user',
    name: 'Emily Davis',
    email: 'emily.davis@company.com',
    department: 'Operations',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-19T16:30:00Z'
  },
  {
    id: 6,
    username: 'security_verifier',
    password: 'security123',
    role: 'user',
    name: 'David Wilson',
    email: 'david.wilson@company.com',
    department: 'Security',
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    lastLogin: '2025-01-20T10:30:00Z'
  }
];

// Mock Roles and Privileges
const mockRoles: Role[] = [
  {
    id: 1,
    name: 'admin',
    description: 'System Administrator with full access',
    privileges: ['approve_po', 'verify_invoice', 'manage_users', 'manage_skus', 'manage_vendors'],
    isActive: true
  },
  {
    id: 2,
    name: 'approver',
    description: 'Purchase Order Approver',
    privileges: ['approve_po', 'view_po_details'],
    isActive: true
  },
  {
    id: 3,
    name: 'quality_inspector',
    description: 'Quality Control Inspector',
    privileges: ['verify_invoice', 'perform_iqc', 'view_po_details'],
    isActive: true
  },
  {
    id: 4,
    name: 'procurement_manager',
    description: 'Procurement Manager',
    privileges: ['approve_po', 'verify_invoice', 'manage_skus', 'manage_vendors', 'view_po_details'],
    isActive: true
  },
  {
    id: 5,
    name: 'security_verifier',
    description: 'Security Verification Specialist',
    privileges: ['verify_invoice', 'view_po_details'],
    isActive: true
  },
  {
    id: 6,
    name: 'user',
    description: 'Regular User',
    privileges: ['view_po_details'],
    isActive: true
  }
];

const mockPrivileges: Privilege[] = [
  { id: 1, name: 'approve_po', description: 'Approve Purchase Orders', resource: 'purchase_orders', action: 'approve' },
  { id: 2, name: 'verify_invoice', description: 'Verify Invoices for Security', resource: 'invoices', action: 'verify' },
  { id: 3, name: 'perform_iqc', description: 'Perform IQC Testing', resource: 'quality_control', action: 'test' },
  { id: 4, name: 'manage_users', description: 'Manage User Accounts', resource: 'users', action: 'manage' },
  { id: 5, name: 'manage_skus', description: 'Manage SKUs', resource: 'skus', action: 'manage' },
  { id: 6, name: 'manage_vendors', description: 'Manage Vendors', resource: 'vendors', action: 'manage' },
  { id: 7, name: 'view_po_details', description: 'View Purchase Order Details', resource: 'purchase_orders', action: 'view' }
];

// Mock Vendors - All Electrical Components and Systems
const mockVendors: Vendor[] = [
  {
    id: 1,
    vendorCode: 'ELEC-001',
    name: 'Electronic Components Co',
    contactDetails: {
      email: 'sales@electrocomp.com',
      phone: '+1-555-0200',
      website: 'https://electrocomp.com',
      address: {
        street: '456 Tech Park Blvd',
        city: 'Silicon Valley',
        state: 'CA',
        zip: '94000',
        country: 'USA'
      },
      primaryContact: {
        name: 'Lisa Chen',
        title: 'Account Manager',
        email: 'lisa.chen@electrocomp.com',
        phone: '+1-555-0201'
      }
    },
    paymentTerms: 'Net 45',
    linkedSKUs: [1, 2, 3, 4, 11, 12, 15],
    performanceRating: 4.6,
    deliveryMetrics: {
      totalOrders: 75,
      onTimeDeliveries: 70,
      qualityScore: 4.5,
      responseTime: 2
    },
    certifications: ['ISO 9001', 'RoHS', 'CE', 'UL Listed'],
    taxInfo: {
      taxId: 'TAX-ELEC-001-2024'
    },
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-18T00:00:00Z'
  },
  {
    id: 2,
    vendorCode: 'ELEC-002',
    name: 'Power Systems Solutions',
    contactDetails: {
      email: 'sales@powersystems.com',
      phone: '+1-555-0300',
      website: 'https://powersystems.com',
      address: {
        street: '789 Electrical Ave',
        city: 'Power City',
        state: 'NY',
        zip: '10001',
        country: 'USA'
      },
      primaryContact: {
        name: 'Michael Rodriguez',
        title: 'Sales Director',
        email: 'michael.rodriguez@powersystems.com',
        phone: '+1-555-0301'
      }
    },
    paymentTerms: 'Net 30',
    linkedSKUs: [5, 6, 7, 9, 10, 13, 14],
    performanceRating: 4.3,
    deliveryMetrics: {
      totalOrders: 60,
      onTimeDeliveries: 55,
      qualityScore: 4.2,
      responseTime: 3
    },
    certifications: ['ISO 9001', 'UL Listed', 'CSA', 'IEEE'],
    taxInfo: {
      taxId: 'TAX-ELEC-002-2024'
    },
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z'
  },
  {
    id: 3,
    vendorCode: 'ELEC-003',
    name: 'Industrial Electronics Supply',
    contactDetails: {
      email: 'orders@industrialelectronics.com',
      phone: '+1-555-0400',
      website: 'https://industrialelectronics.com',
      address: {
        street: '321 Circuit Blvd',
        city: 'Electric City',
        state: 'TX',
        zip: '75000',
        country: 'USA'
      },
      primaryContact: {
        name: 'Sarah Thompson',
        title: 'Procurement Specialist',
        email: 'sarah.thompson@industrialelectronics.com',
        phone: '+1-555-0401'
      }
    },
    paymentTerms: 'Net 15',
    linkedSKUs: [8, 9, 10, 11, 12, 13, 14, 15],
    performanceRating: 4.1,
    deliveryMetrics: {
      totalOrders: 45,
      onTimeDeliveries: 40,
      qualityScore: 4.0,
      responseTime: 4
    },
    certifications: ['ISO 9001', 'IEC 61000', 'NEMA'],
    taxInfo: {
      taxId: 'TAX-ELEC-003-2024'
    },
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-20T00:00:00Z'
  },
  {
    id: 4,
    vendorCode: 'ELEC-004',
    name: 'TechFlow Electrical Distributors',
    contactDetails: {
      email: 'sales@techflow.com',
      phone: '+1-555-0500',
      website: 'https://techflow.com',
      address: {
        street: '125 Innovation Drive',
        city: 'Tech Valley',
        state: 'WA',
        zip: '98000',
        country: 'USA'
      },
      primaryContact: {
        name: 'David Kim',
        title: 'Regional Sales Manager',
        email: 'david.kim@techflow.com',
        phone: '+1-555-0501'
      }
    },
    paymentTerms: 'Net 30',
    linkedSKUs: [1, 2, 3, 4, 5, 8, 11, 15],
    performanceRating: 4.4,
    deliveryMetrics: {
      totalOrders: 85,
      onTimeDeliveries: 78,
      qualityScore: 4.3,
      responseTime: 3
    },
    certifications: ['ISO 9001', 'RoHS', 'CE', 'FCC'],
    taxInfo: {
      taxId: 'TAX-ELEC-004-2024'
    },
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-22T00:00:00Z'
  },
  {
    id: 5,
    vendorCode: 'ELEC-005',
    name: 'Apex Electrical Components',
    contactDetails: {
      email: 'info@apexelectrical.com',
      phone: '+1-555-0600',
      website: 'https://apexelectrical.com',
      address: {
        street: '890 Component Way',
        city: 'Electronics Hub',
        state: 'FL',
        zip: '33000',
        country: 'USA'
      },
      primaryContact: {
        name: 'Jennifer Martinez',
        title: 'Business Development Manager',
        email: 'jennifer.martinez@apexelectrical.com',
        phone: '+1-555-0601'
      }
    },
    paymentTerms: 'Net 60',
    linkedSKUs: [6, 7, 9, 10, 12, 13, 14],
    performanceRating: 4.2,
    deliveryMetrics: {
      totalOrders: 52,
      onTimeDeliveries: 47,
      qualityScore: 4.1,
      responseTime: 5
    },
    certifications: ['ISO 9001', 'UL Listed', 'CSA', 'ETL'],
    taxInfo: {
      taxId: 'TAX-ELEC-005-2024'


      
    },
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-19T00:00:00Z'
  }
];

// Mock SKUs - FIX: Updated all dates to 2025 for consistency
const mockSKUs: SKU[] = [
  {
    id: 1,
    skuCode: 'SKU-001',
    name: 'Resistor 10K Ohm',
    description: '1/4W Carbon Film Resistor, 5% tolerance',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 2500,
    reorderLevel: 1000,
    maxStock: 10000,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 3.50, leadTime: 14, minimumOrderQty: 1000, lastUpdated: '2025-09-18T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 3.25, leadTime: 10, minimumOrderQty: 2000, lastUpdated: '2025-09-20T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 1, vendorId: 1, price: 3.25, date: '2025-09-01T00:00:00Z', changedBy: 1 },
      { id: 2, vendorId: 1, price: 3.50, date: '2025-09-18T00:00:00Z', changedBy: 1, reason: 'Carbon film price increase' },
      { id: 3, vendorId: 4, price: 3.75, date: '2025-09-01T00:00:00Z', changedBy: 1 },
      { id: 4, vendorId: 4, price: 3.25, date: '2025-09-20T00:00:00Z', changedBy: 1, reason: 'Bulk discount program' }
    ],
    isActive: true,
    createdAt: '2025-09-01T00:00:00Z',
    updatedAt: '2025-09-18T00:00:00Z',
    createdBy: 1,
    specifications: {
      tolerance: '5%',
      powerRating: '0.25W',
      temperature: '-55°C to +155°C'
    }
  },
  {
    id: 2,
    skuCode: 'SKU-002',
    name: 'Capacitor 100uF',
    description: 'Electrolytic Capacitor 100uF 25V',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 800,
    reorderLevel: 200,
    maxStock: 3000,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 12.50, leadTime: 10, minimumOrderQty: 500, lastUpdated: '2025-01-16T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 11.75, leadTime: 12, minimumOrderQty: 300, lastUpdated: '2025-01-22T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 5, vendorId: 1, price: 13.25, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 6, vendorId: 1, price: 12.50, date: '2025-01-16T00:00:00Z', changedBy: 1, reason: 'Volume discount' },
      { id: 7, vendorId: 4, price: 12.75, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 8, vendorId: 4, price: 11.75, date: '2025-01-22T00:00:00Z', changedBy: 1, reason: 'Competitive pricing' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-16T00:00:00Z',
    createdBy: 1,
    specifications: {
      capacitance: '100uF',
      voltage: '25V',
      type: 'Electrolytic',
      temperature: '-40°C to +85°C',
      tolerance: '±20%'
    }
  },
  {
    id: 3,
    skuCode: 'SKU-003',
    name: 'LED 5mm Red',
    description: 'High brightness red LED, 5mm package',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 1200,
    reorderLevel: 300,
    maxStock: 5000,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 5.25, leadTime: 12, minimumOrderQty: 1000, lastUpdated: '2025-01-14T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 4.95, leadTime: 8, minimumOrderQty: 500, lastUpdated: '2025-01-19T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 9, vendorId: 1, price: 5.25, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 10, vendorId: 4, price: 5.15, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 11, vendorId: 4, price: 4.95, date: '2025-01-19T00:00:00Z', changedBy: 1, reason: 'Volume pricing improvement' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-14T00:00:00Z',
    createdBy: 1,
    specifications: {
      color: 'Red',
      diameter: '5mm',
      brightness: '2000mcd',
      forwardVoltage: '2.0V',
      forwardCurrent: '20mA',
      viewingAngle: '30°'
    }
  },
  {
    id: 4,
    skuCode: 'SKU-004',
    name: 'Microcontroller ATmega328P',
    description: '8-bit AVR microcontroller, 32KB Flash, DIP package',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 150,
    reorderLevel: 50,
    maxStock: 500,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 185.00, leadTime: 21, minimumOrderQty: 100, lastUpdated: '2025-01-15T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 175.50, leadTime: 18, minimumOrderQty: 50, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 12, vendorId: 1, price: 175.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 13, vendorId: 1, price: 185.00, date: '2025-01-15T00:00:00Z', changedBy: 1, reason: 'Supply chain adjustment' },
      { id: 14, vendorId: 4, price: 190.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 15, vendorId: 4, price: 175.50, date: '2025-01-18T00:00:00Z', changedBy: 1, reason: 'Competitive pricing' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z',
    createdBy: 1,
    specifications: {
      flash: '32KB',
      sram: '2KB',
      eeprom: '1KB',
      package: 'DIP-28',
      voltage: '1.8V - 5.5V'
    }
  },
  {
    id: 5,
    skuCode: 'SKU-005',
    name: 'Circuit Breaker 20A',
    description: 'Single pole circuit breaker, 20A rating, C curve',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 45,
    reorderLevel: 20,
    maxStock: 200,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 1250.00, leadTime: 14, minimumOrderQty: 25, lastUpdated: '2025-01-12T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 1225.00, leadTime: 16, minimumOrderQty: 20, lastUpdated: '2025-01-14T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 16, vendorId: 2, price: 1180.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 17, vendorId: 2, price: 1250.00, date: '2025-01-12T00:00:00Z', changedBy: 1, reason: 'Material cost increase' },
      { id: 18, vendorId: 4, price: 1275.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 19, vendorId: 4, price: 1225.00, date: '2025-01-14T00:00:00Z', changedBy: 1, reason: 'Market competition' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-12T00:00:00Z',
    createdBy: 1,
    specifications: {
      rating: '20A',
      curve: 'C',
      voltage: '240V',
      breakingCapacity: '6kA'
    }
  },
  {
    id: 6,
    skuCode: 'SKU-006',
    name: 'Electrical Wire 12 AWG',
    description: 'Copper electrical wire, 12 AWG, THWN-2, 500ft roll',
    category: 'Electrical',
    unit: 'rolls',
    currentStock: 25,
    reorderLevel: 10,
    maxStock: 100,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 4575.00, leadTime: 7, minimumOrderQty: 5, lastUpdated: '2025-01-10T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 4490.00, leadTime: 10, minimumOrderQty: 10, lastUpdated: '2025-01-16T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 20, vendorId: 2, price: 4320.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 21, vendorId: 2, price: 4575.00, date: '2025-01-10T00:00:00Z', changedBy: 1, reason: 'Copper price increase' },
      { id: 22, vendorId: 5, price: 4650.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 23, vendorId: 5, price: 4490.00, date: '2025-01-16T00:00:00Z', changedBy: 1, reason: 'Volume discount' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z',
    createdBy: 1,
    specifications: {
      gauge: '12 AWG',
      conductor: 'Copper',
      insulation: 'THWN-2',
      length: '500ft',
      color: 'Black'
    }
  },
  {
    id: 7,
    skuCode: 'SKU-007',
    name: 'Power Supply 24V 5A',
    description: 'Switching power supply, 24V DC, 5A output, DIN rail mount',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 35,
    reorderLevel: 15,
    maxStock: 150,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 2890.00, leadTime: 21, minimumOrderQty: 10, lastUpdated: '2025-01-20T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 2975.00, leadTime: 18, minimumOrderQty: 5, lastUpdated: '2025-01-17T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 24, vendorId: 2, price: 2890.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 25, vendorId: 5, price: 3120.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 26, vendorId: 5, price: 2975.00, date: '2025-01-17T00:00:00Z', changedBy: 1, reason: 'Competitive adjustment' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-20T00:00:00Z',
    createdBy: 1,
    specifications: {
      output: '24V DC',
      current: '5A',
      input: '100-240V AC',
      efficiency: '>85%',
      mounting: 'DIN Rail'
    }
  },
  {
    id: 8,
    skuCode: 'SKU-008',
    name: 'Relay Module 8 Channel',
    description: '8-channel relay module, 5V control, 10A per channel',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 20,
    reorderLevel: 8,
    maxStock: 80,
    linkedVendors: [
      { vendorId: 3, defaultPrice: 1850.00, leadTime: 14, minimumOrderQty: 5, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 1795.00, leadTime: 12, minimumOrderQty: 10, lastUpdated: '2025-01-21T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 27, vendorId: 3, price: 1850.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 28, vendorId: 4, price: 1925.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 29, vendorId: 4, price: 1795.00, date: '2025-01-21T00:00:00Z', changedBy: 1, reason: 'Price match offer' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-18T00:00:00Z',
    createdBy: 1,
    specifications: {
      channels: 8,
      controlVoltage: '5V',
      contactRating: '10A',
      isolation: 'Opto-isolated',
      interface: 'TTL'
    }
  },
  {
    id: 9,
    skuCode: 'SKU-009',
    name: 'Motor Starter 3HP',
    description: '3-phase motor starter, 3HP, 230V, with overload protection',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 12,
    reorderLevel: 5,
    maxStock: 50,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 12500.00, leadTime: 28, minimumOrderQty: 3, lastUpdated: '2025-01-20T00:00:00Z', isPreferred: true },
      { vendorId: 3, defaultPrice: 13250.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false },
      { vendorId: 5, defaultPrice: 12875.00, leadTime: 25, minimumOrderQty: 2, lastUpdated: '2025-01-22T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 30, vendorId: 2, price: 12000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 31, vendorId: 2, price: 12500.00, date: '2025-01-20T00:00:00Z', changedBy: 1, reason: 'Material cost increase' },
      { id: 32, vendorId: 3, price: 13500.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 33, vendorId: 3, price: 13250.00, date: '2025-01-18T00:00:00Z', changedBy: 1, reason: 'Competitive pricing' },
      { id: 34, vendorId: 5, price: 13000.00, date: '2025-01-10T00:00:00Z', changedBy: 1 },
      { id: 35, vendorId: 5, price: 12875.00, date: '2025-01-22T00:00:00Z', changedBy: 1, reason: 'Volume incentive' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-20T00:00:00Z',
    createdBy: 1,
    specifications: {
      hp: '3HP',
      voltage: '230V',
      phases: '3',
      protection: 'Overload',
      mounting: 'Panel Mount'
    }
  },
  {
    id: 10,
    skuCode: 'SKU-010',
    name: 'Transformer 480V-120V 5KVA',
    description: 'Step-down transformer, 480V primary to 120V secondary, 5KVA',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 8,
    reorderLevel: 3,
    maxStock: 25,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 28500.00, leadTime: 35, minimumOrderQty: 2, lastUpdated: '2025-01-22T00:00:00Z', isPreferred: true },
      { vendorId: 3, defaultPrice: 29500.00, leadTime: 42, minimumOrderQty: 1, lastUpdated: '2025-01-20T00:00:00Z', isPreferred: false },
      { vendorId: 5, defaultPrice: 27850.00, leadTime: 30, minimumOrderQty: 3, lastUpdated: '2025-01-25T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 36, vendorId: 2, price: 27500.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 37, vendorId: 2, price: 28500.00, date: '2025-01-22T00:00:00Z', changedBy: 1, reason: 'Copper price increase' },
      { id: 38, vendorId: 3, price: 29000.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 39, vendorId: 3, price: 29500.00, date: '2025-01-20T00:00:00Z', changedBy: 1, reason: 'Premium quality upgrade' },
      { id: 40, vendorId: 5, price: 28200.00, date: '2025-01-08T00:00:00Z', changedBy: 1 },
      { id: 41, vendorId: 5, price: 27850.00, date: '2025-01-25T00:00:00Z', changedBy: 1, reason: 'Market adjustment' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-22T00:00:00Z',
    createdBy: 1,
    specifications: {
      primaryVoltage: '480V',
      secondaryVoltage: '120V',
      kva: '5KVA',
      frequency: '60Hz',
      phases: '3',
      mounting: 'Floor/Wall',
      efficiency: '>95%'
    }
  },
  {
    id: 11,
    skuCode: 'SKU-011',
    name: 'Contactor 30A 3-Pole',
    description: '3-pole contactor, 30A, 120V coil, normally open',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 35,
    reorderLevel: 15,
    maxStock: 100,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 4250.00, leadTime: 14, minimumOrderQty: 10, lastUpdated: '2025-01-19T00:00:00Z', isPreferred: true },
      { vendorId: 2, defaultPrice: 4575.00, leadTime: 10, minimumOrderQty: 5, lastUpdated: '2025-01-17T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 42, vendorId: 1, price: 4000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 43, vendorId: 1, price: 4250.00, date: '2025-01-19T00:00:00Z', changedBy: 1, reason: 'Material cost adjustment' },
      { id: 44, vendorId: 2, price: 4700.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 45, vendorId: 2, price: 4575.00, date: '2025-01-17T00:00:00Z', changedBy: 1, reason: 'Volume discount applied' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-19T00:00:00Z',
    createdBy: 1,
    specifications: {
      current: '30A',
      poles: '3',
      coilVoltage: '120V',
      contactType: 'Normally Open',
      mounting: 'DIN Rail',
      electricalLife: '1M cycles'
    }
  },
  {
    id: 12,
    skuCode: 'SKU-012',
    name: 'Panel Meter Digital Voltmeter',
    description: 'Digital panel voltmeter, 0-500V AC/DC, LED display',
    category: 'Electronics',
    unit: 'pieces',
    currentStock: 22,
    reorderLevel: 8,
    maxStock: 60,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 3890.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-16T00:00:00Z', isPreferred: true },
      { vendorId: 3, defaultPrice: 4125.00, leadTime: 28, minimumOrderQty: 3, lastUpdated: '2025-01-21T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 46, vendorId: 1, price: 3650.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 47, vendorId: 1, price: 3890.00, date: '2025-01-16T00:00:00Z', changedBy: 1, reason: 'Technology upgrade' },
      { id: 48, vendorId: 3, price: 4200.00, date: '2025-01-05T00:00:00Z', changedBy: 1 },
      { id: 49, vendorId: 3, price: 4125.00, date: '2025-01-21T00:00:00Z', changedBy: 1, reason: 'Competitive adjustment' }
    ],
    isActive: true,
    createdAt: '2025-01-05T00:00:00Z',
    updatedAt: '2025-01-21T00:00:00Z',
    createdBy: 1,
    specifications: {
      range: '0-500V',
      type: 'AC/DC',
      display: 'LED',
      accuracy: '±0.5%',
      digits: '3.5',
      mounting: 'Panel',
      powerSupply: '85-265V AC'
    }
  },
  {
    id: 13,
    skuCode: 'SKU-013',
    name: 'Conduit PVC 3/4 inch',
    description: 'PVC electrical conduit, 3/4 inch diameter, 10ft length',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 120,
    reorderLevel: 50,
    maxStock: 300,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 385.00, leadTime: 7, minimumOrderQty: 50, lastUpdated: '2025-01-14T00:00:00Z', isPreferred: true },
      { vendorId: 3, defaultPrice: 410.00, leadTime: 10, minimumOrderQty: 25, lastUpdated: '2025-01-12T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 50, vendorId: 2, price: 365.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 51, vendorId: 2, price: 385.00, date: '2025-01-14T00:00:00Z', changedBy: 1, reason: 'PVC resin cost increase' },
      { id: 52, vendorId: 3, price: 400.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 53, vendorId: 3, price: 410.00, date: '2025-01-12T00:00:00Z', changedBy: 1, reason: 'Transportation cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-14T00:00:00Z',
    createdBy: 1,
    specifications: {
      diameter: '3/4 inch',
      length: '10ft',
      material: 'PVC',
      standard: 'UL Listed',
      temperature: '-40°F to +176°F',
      color: 'Gray'
    }
  },
  {
    id: 14,
    skuCode: 'SKU-014',
    name: 'Junction Box 4x4 Metal',
    description: 'Metal electrical junction box, 4x4 inch, with cover',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 85,
    reorderLevel: 30,
    maxStock: 200,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 795.00, leadTime: 12, minimumOrderQty: 25, lastUpdated: '2025-01-20T00:00:00Z', isPreferred: true },
      { vendorId: 3, defaultPrice: 825.00, leadTime: 14, minimumOrderQty: 20, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 54, vendorId: 3, price: 800.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 55, vendorId: 3, price: 825.00, date: '2025-01-18T00:00:00Z', changedBy: 1, reason: 'Steel price increase' },
      { id: 56, vendorId: 2, price: 820.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 57, vendorId: 2, price: 795.00, date: '2025-01-20T00:00:00Z', changedBy: 1, reason: 'Bulk purchase discount' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-20T00:00:00Z',
    createdBy: 1,
    specifications: {
      size: '4x4 inch',
      depth: '1.5 inch',
      material: 'Galvanized Steel',
      knockouts: '8',
      cover: 'Included',
      standard: 'UL Listed'
    }
  },
  {
    id: 15,
    skuCode: 'SKU-015',
    name: 'Fuse 15A Fast-Acting',
    description: 'Fast-acting fuse, 15A, 250V, ceramic body',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 450,
    reorderLevel: 200,
    maxStock: 1000,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 215.00, leadTime: 10, minimumOrderQty: 100, lastUpdated: '2025-01-15T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 208.00, leadTime: 12, minimumOrderQty: 200, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 58, vendorId: 1, price: 205.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 59, vendorId: 1, price: 215.00, date: '2025-01-15T00:00:00Z', changedBy: 1, reason: 'Silver contact price increase' },
      { id: 60, vendorId: 4, price: 212.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 61, vendorId: 4, price: 208.00, date: '2025-01-18T00:00:00Z', changedBy: 1, reason: 'Volume discount program' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z',
    createdBy: 1,
    specifications: {
      current: '15A',
      voltage: '250V',
      type: 'Fast-Acting',
      body: 'Ceramic',
      standard: 'UL Listed',
      breakingCapacity: '10kA'
    }
  },
  {
    id: 16,
    skuCode: 'SKU-016',
    name: 'Motor Starter 3HP',
    description: '3-phase motor starter, 3HP, 240V, NEMA 1 enclosure',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 12,
    reorderLevel: 5,
    maxStock: 50,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 18550.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-15T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 19200.00, leadTime: 18, minimumOrderQty: 3, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 50, vendorId: 2, price: 18000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 51, vendorId: 2, price: 18550.00, date: '2025-01-15T00:00:00Z', changedBy: 1, reason: 'Component cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-15T00:00:00Z',
    createdBy: 1,
    specifications: {
      horsepower: '3HP',
      voltage: '240V',
      phase: '3-Phase',
      enclosure: 'NEMA 1',
      coilVoltage: '24V DC',
      contacts: '3NO + 1NC'
    }
  },
  {
    id: 17,
    skuCode: 'SKU-017',
    name: 'VFD 5HP 480V',
    description: 'Variable Frequency Drive, 5HP, 480V, 3-phase input/output',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 8,
    reorderLevel: 3,
    maxStock: 25,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 42500.00, leadTime: 28, minimumOrderQty: 2, lastUpdated: '2025-01-12T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 44500.00, leadTime: 25, minimumOrderQty: 1, lastUpdated: '2025-01-16T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 52, vendorId: 2, price: 41000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 53, vendorId: 2, price: 42500.00, date: '2025-01-12T00:00:00Z', changedBy: 1, reason: 'Semiconductor shortage impact' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-12T00:00:00Z',
    createdBy: 1,
    specifications: {
      horsepower: '5HP',
      inputVoltage: '480V',
      outputVoltage: '0-480V',
      frequency: '0.1-400Hz',
      enclosure: 'NEMA 1',
      control: 'Keypad + Digital I/O'
    }
  },
  {
    id: 18,
    skuCode: 'SKU-018',
    name: 'Electrical Conduit 1/2 inch',
    description: 'Rigid steel conduit, 1/2 inch, 10ft length, galvanized',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 150,
    reorderLevel: 50,
    maxStock: 500,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 875.00, leadTime: 7, minimumOrderQty: 20, lastUpdated: '2025-01-10T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 850.00, leadTime: 10, minimumOrderQty: 25, lastUpdated: '2025-01-14T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 54, vendorId: 2, price: 825.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 55, vendorId: 2, price: 875.00, date: '2025-01-10T00:00:00Z', changedBy: 1, reason: 'Steel price increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-10T00:00:00Z',
    createdBy: 1,
    specifications: {
      size: '1/2 inch',
      material: 'Steel',
      coating: 'Galvanized',
      length: '10ft',
      wallThickness: '0.109 inch',
      standard: 'NEC compliant'
    }
  },
  {
    id: 19,
    skuCode: 'SKU-019',
    name: 'Light Switch 2-Way',
    description: 'Single pole double throw switch, 15A, 120V, white',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 200,
    reorderLevel: 75,
    maxStock: 800,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 325.00, leadTime: 5, minimumOrderQty: 50, lastUpdated: '2025-01-08T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 315.00, leadTime: 7, minimumOrderQty: 40, lastUpdated: '2025-01-12T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 56, vendorId: 2, price: 325.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 57, vendorId: 5, price: 320.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 58, vendorId: 5, price: 315.00, date: '2025-01-12T00:00:00Z', changedBy: 1, reason: 'Volume pricing' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-08T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: 'SPDT',
      rating: '15A',
      voltage: '120V',
      color: 'White',
      terminals: 'Side-wire + Back-wire',
      standard: 'UL Listed'
    }
  },
  {
    id: 20,
    skuCode: 'SKU-020',
    name: 'Electrical Outlet GFCI',
    description: 'Ground Fault Circuit Interrupter outlet, 20A, 120V, tamper-resistant',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 85,
    reorderLevel: 30,
    maxStock: 300,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 1850.00, leadTime: 10, minimumOrderQty: 25, lastUpdated: '2025-01-11T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 1795.00, leadTime: 12, minimumOrderQty: 20, lastUpdated: '2025-01-15T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 59, vendorId: 2, price: 1725.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 60, vendorId: 2, price: 1850.00, date: '2025-01-11T00:00:00Z', changedBy: 1, reason: 'Safety component cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-11T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: 'GFCI',
      rating: '20A',
      voltage: '120V',
      features: 'Tamper-resistant',
      testReset: 'Yes',
      standard: 'UL Listed'
    }
  },
  {
    id: 21,
    skuCode: 'SKU-021',
    name: 'Cable Tray 12 inch',
    description: 'Steel cable tray, 12 inch width, 6ft length, ladder type',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 45,
    reorderLevel: 15,
    maxStock: 150,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 12500.00, leadTime: 14, minimumOrderQty: 10, lastUpdated: '2025-01-13T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 12850.00, leadTime: 16, minimumOrderQty: 8, lastUpdated: '2025-01-17T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 61, vendorId: 2, price: 12000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 62, vendorId: 2, price: 12500.00, date: '2025-01-13T00:00:00Z', changedBy: 1, reason: 'Steel fabrication cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-13T00:00:00Z',
    createdBy: 1,
    specifications: {
      width: '12 inch',
      length: '6ft',
      type: 'Ladder',
      material: 'Steel',
      coating: 'Galvanized',
      rungSpacing: '9 inch'
    }
  },
  {
    id: 22,
    skuCode: 'SKU-022',
    name: 'Proximity Sensor 24V',
    description: 'Inductive proximity sensor, 24V DC, NPN, 8mm sensing distance',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 120,
    reorderLevel: 40,
    maxStock: 400,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 2850.00, leadTime: 14, minimumOrderQty: 25, lastUpdated: '2025-01-09T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 2725.00, leadTime: 12, minimumOrderQty: 30, lastUpdated: '2025-01-13T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 63, vendorId: 1, price: 2850.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 64, vendorId: 4, price: 2800.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 65, vendorId: 4, price: 2725.00, date: '2025-01-13T00:00:00Z', changedBy: 1, reason: 'Sensor technology improvement' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-09T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: 'Inductive',
      voltage: '24V DC',
      output: 'NPN',
      sensingDistance: '8mm',
      frequency: '1kHz',
      housing: 'M18 x 1'
    }
  },
  {
    id: 23,
    skuCode: 'SKU-023',
    name: 'Emergency Stop Button',
    description: 'Emergency stop push button, mushroom head, 22mm, red, NC contact',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 35,
    reorderLevel: 15,
    maxStock: 100,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 4500.00, leadTime: 10, minimumOrderQty: 10, lastUpdated: '2025-01-07T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 4350.00, leadTime: 12, minimumOrderQty: 8, lastUpdated: '2025-01-11T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 66, vendorId: 2, price: 4500.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 67, vendorId: 5, price: 4525.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 68, vendorId: 5, price: 4350.00, date: '2025-01-11T00:00:00Z', changedBy: 1, reason: 'Safety equipment discount' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-07T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: 'Emergency Stop',
      head: 'Mushroom',
      size: '22mm',
      color: 'Red',
      contact: 'NC',
      voltage: '24V DC',
      standard: 'IEC 60947-5-5'
    }
  },
  {
    id: 24,
    skuCode: 'SKU-024',
    name: 'Control Panel Enclosure',
    description: 'NEMA 4X stainless steel control panel, 24x18x8 inch, IP65',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 18,
    reorderLevel: 8,
    maxStock: 60,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 28500.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-14T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 29500.00, leadTime: 25, minimumOrderQty: 3, lastUpdated: '2025-01-18T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 69, vendorId: 2, price: 27500.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 70, vendorId: 2, price: 28500.00, date: '2025-01-14T00:00:00Z', changedBy: 1, reason: 'Stainless steel cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-14T00:00:00Z',
    createdBy: 1,
    specifications: {
      material: 'Stainless Steel',
      rating: 'NEMA 4X',
      protection: 'IP65',
      dimensions: '24x18x8 inch',
      door: 'Hinged',
      mounting: 'Wall mount'
    }
  },
  {
    id: 25,
    skuCode: 'SKU-025',
    name: 'PLC Module Digital Input',
    description: '16-point digital input module, 24V DC, sinking/sourcing',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 25,
    reorderLevel: 10,
    maxStock: 80,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 12500.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-16T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 12850.00, leadTime: 18, minimumOrderQty: 3, lastUpdated: '2025-01-19T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 71, vendorId: 1, price: 12000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 72, vendorId: 1, price: 12500.00, date: '2025-01-16T00:00:00Z', changedBy: 1, reason: 'PLC component shortage' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-16T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: 'Digital Input',
      points: '16',
      voltage: '24V DC',
      inputType: 'Sinking/Sourcing',
      isolation: 'Optical',
      responseTime: '1ms'
    }
  },
  {
    id: 26,
    skuCode: 'SKU-026',
    name: 'HMI Touch Screen 7 inch',
    description: '7-inch color touch screen HMI, 800x480 resolution, Ethernet',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 15,
    reorderLevel: 6,
    maxStock: 40,
    linkedVendors: [
      { vendorId: 1, defaultPrice: 42500.00, leadTime: 28, minimumOrderQty: 2, lastUpdated: '2025-01-17T00:00:00Z', isPreferred: true },
      { vendorId: 4, defaultPrice: 44500.00, leadTime: 25, minimumOrderQty: 1, lastUpdated: '2025-01-20T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 73, vendorId: 1, price: 41000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 74, vendorId: 1, price: 42500.00, date: '2025-01-17T00:00:00Z', changedBy: 1, reason: 'Display panel cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-17T00:00:00Z',
    createdBy: 1,
    specifications: {
      size: '7 inch',
      resolution: '800x480',
      display: 'TFT Color',
      touch: 'Resistive',
      communication: 'Ethernet',
      memory: '128MB'
    }
  },
  {
    id: 27,
    skuCode: 'SKU-027',
    name: 'Cable Gland M20',
    description: 'Cable gland, M20 thread, IP68, brass body, EMC shielded',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 200,
    reorderLevel: 75,
    maxStock: 600,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 425.00, leadTime: 7, minimumOrderQty: 50, lastUpdated: '2025-01-06T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 410.00, leadTime: 10, minimumOrderQty: 40, lastUpdated: '2025-01-10T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 75, vendorId: 2, price: 425.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 76, vendorId: 5, price: 415.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 77, vendorId: 5, price: 410.00, date: '2025-01-10T00:00:00Z', changedBy: 1, reason: 'Brass commodity pricing' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-06T00:00:00Z',
    createdBy: 1,
    specifications: {
      thread: 'M20',
      material: 'Brass',
      protection: 'IP68',
      shielding: 'EMC',
      cableRange: '6-12mm',
      standard: 'IEC 62444'
    }
  },
  {
    id: 28,
    skuCode: 'SKU-028',
    name: 'Terminal Block 12 Position',
    description: 'Screw terminal block, 12 positions, 5mm pitch, 32A',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 150,
    reorderLevel: 50,
    maxStock: 400,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 850.00, leadTime: 5, minimumOrderQty: 25, lastUpdated: '2025-01-05T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 825.00, leadTime: 7, minimumOrderQty: 30, lastUpdated: '2025-01-09T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 78, vendorId: 2, price: 850.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 79, vendorId: 5, price: 835.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 80, vendorId: 5, price: 825.00, date: '2025-01-09T00:00:00Z', changedBy: 1, reason: 'Terminal block volume pricing' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-05T00:00:00Z',
    createdBy: 1,
    specifications: {
      positions: '12',
      pitch: '5mm',
      current: '32A',
      voltage: '600V',
      connection: 'Screw',
      material: 'PA66'
    }
  },
  {
    id: 29,
    skuCode: 'SKU-029',
    name: 'Power Meter 3-Phase',
    description: 'Digital power meter, 3-phase, 480V, Modbus RTU communication',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 22,
    reorderLevel: 8,
    maxStock: 60,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 18500.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-13T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 19250.00, leadTime: 18, minimumOrderQty: 3, lastUpdated: '2025-01-17T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 81, vendorId: 2, price: 18000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 82, vendorId: 2, price: 18500.00, date: '2025-01-13T00:00:00Z', changedBy: 1, reason: 'Metering technology update' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-13T00:00:00Z',
    createdBy: 1,
    specifications: {
      type: '3-Phase',
      voltage: '480V',
      communication: 'Modbus RTU',
      display: 'LCD',
      accuracy: '0.5%',
      mounting: 'DIN rail'
    }
  },
  {
    id: 30,
    skuCode: 'SKU-030',
    name: 'Lightning Arrester 12kV',
    description: 'Surge arrester, 12kV, distribution class, polymer housed',
    category: 'Electrical',
    unit: 'pieces',
    currentStock: 18,
    reorderLevel: 6,
    maxStock: 50,
    linkedVendors: [
      { vendorId: 2, defaultPrice: 12500.00, leadTime: 21, minimumOrderQty: 5, lastUpdated: '2025-01-11T00:00:00Z', isPreferred: true },
      { vendorId: 5, defaultPrice: 13250.00, leadTime: 25, minimumOrderQty: 3, lastUpdated: '2025-01-15T00:00:00Z', isPreferred: false }
    ],
    priceHistory: [
      { id: 83, vendorId: 2, price: 12000.00, date: '2025-01-01T00:00:00Z', changedBy: 1 },
      { id: 84, vendorId: 2, price: 12500.00, date: '2025-01-11T00:00:00Z', changedBy: 1, reason: 'Polymer housing cost increase' }
    ],
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-01-11T00:00:00Z',
    createdBy: 1,
    specifications: {
      voltage: '12kV',
      class: 'Distribution',
      housing: 'Polymer',
      discharge: '10kA',
      standard: 'IEEE C62.11',
      mounting: 'Bracket'
    }
  },
  // Additional SKUs for comprehensive showcase
  {
    id: 7,
    skuCode: 'SKU-007',
    name: 'Capacitor 1000µF 25V',
    description: 'High-quality electrolytic capacitor for power supply applications',
    category: 'Passive Components',
    unit: 'pcs',
    currentStock: 1200,
    reorderLevel: 200,
    maxStock: 2000,
    linkedVendors: [
      {
        vendorId: 1,
        defaultPrice: 2.25,
        leadTime: 7,
        minimumOrderQty: 100,
        lastUpdated: '2025-02-15T00:00:00Z',
        isPreferred: true,
      }
    ],
    priceHistory: [
      { id: 1, date: '2025-01-01T00:00:00Z', price: 2.20, vendorId: 1, changedBy: 1 },
      { id: 2, date: '2025-02-01T00:00:00Z', price: 2.25, vendorId: 1, changedBy: 1 }
    ],
    lastPurchasePrice: 2.25,
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-02-15T00:00:00Z',
    createdBy: 1,
    specifications: {
      capacitance: '1000µF',
      voltage: '25V',
      tolerance: '±20%',
      temperature: '-40°C to +85°C',
      package: 'Radial',
      manufacturer: 'Panasonic'
    }
  },
  {
    id: 8,
    skuCode: 'SKU-008',
    name: 'Relay Module 12V 10A',
    description: 'High-quality relay module for switching applications',
    category: 'Electromechanical',
    unit: 'pcs',
    currentStock: 150,
    reorderLevel: 25,
    maxStock: 300,
    linkedVendors: [
      {
        vendorId: 2,
        defaultPrice: 15.75,
        leadTime: 10,
        minimumOrderQty: 10,
        lastUpdated: '2025-03-01T00:00:00Z',
        isPreferred: true,
      }
    ],
    priceHistory: [
      { id: 3, date: '2025-01-01T00:00:00Z', price: 15.50, vendorId: 2, changedBy: 1 },
      { id: 4, date: '2025-03-01T00:00:00Z', price: 15.75, vendorId: 2, changedBy: 1 }
    ],
    lastPurchasePrice: 15.75,
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    createdBy: 1,
    specifications: {
      voltage: '12V DC',
      current: '10A',
      contacts: 'SPDT',
      coil: '12V DC',
      package: 'PCB Mount',
      manufacturer: 'Omron'
    }
  },
  {
    id: 9,
    skuCode: 'SKU-009',
    name: 'Digital Multimeter',
    description: 'Professional-grade digital multimeter for electrical testing',
    category: 'Test Equipment',
    unit: 'pcs',
    currentStock: 45,
    reorderLevel: 10,
    maxStock: 100,
    linkedVendors: [
      {
        vendorId: 2,
        defaultPrice: 22.50,
        leadTime: 14,
        minimumOrderQty: 5,
        lastUpdated: '2025-03-01T00:00:00Z',
        isPreferred: true,
      }
    ],
    priceHistory: [
      { id: 5, date: '2025-01-01T00:00:00Z', price: 22.00, vendorId: 2, changedBy: 1 },
      { id: 6, date: '2025-03-01T00:00:00Z', price: 22.50, vendorId: 2, changedBy: 1 }
    ],
    lastPurchasePrice: 22.50,
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    createdBy: 1,
    specifications: {
      display: '3.5 digit LCD',
      voltage: 'DC: 0-1000V, AC: 0-750V',
      current: 'DC: 0-10A, AC: 0-10A',
      resistance: '0-40MΩ',
      features: 'Auto-ranging, Data Hold',
      manufacturer: 'Fluke'
    }
  },
  {
    id: 10,
    skuCode: 'SKU-010',
    name: 'Resistor 1K Ohm 1/4W',
    description: 'Standard carbon film resistor for general purpose applications',
    category: 'Passive Components',
    unit: 'pcs',
    currentStock: 5000,
    reorderLevel: 1000,
    maxStock: 10000,
    linkedVendors: [
      {
        vendorId: 3,
        defaultPrice: 0.85,
        leadTime: 5,
        minimumOrderQty: 1000,
        lastUpdated: '2025-03-01T00:00:00Z',
        isPreferred: true,
      }
    ],
    priceHistory: [
      { id: 7, date: '2025-01-01T00:00:00Z', price: 0.80, vendorId: 3, changedBy: 1 },
      { id: 8, date: '2025-03-01T00:00:00Z', price: 0.85, vendorId: 3, changedBy: 1 }
    ],
    lastPurchasePrice: 0.85,
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    createdBy: 1,
    specifications: {
      resistance: '1KΩ',
      power: '1/4W',
      tolerance: '±5%',
      temperature: '-55°C to +155°C',
      package: 'Axial',
      manufacturer: 'Yageo'
    }
  },
  {
    id: 11,
    skuCode: 'SKU-011',
    name: 'Oscilloscope Probe 100MHz',
    description: 'High-frequency oscilloscope probe for signal analysis',
    category: 'Test Equipment',
    unit: 'pcs',
    currentStock: 25,
    reorderLevel: 5,
    maxStock: 50,
    linkedVendors: [
      {
        vendorId: 1,
        defaultPrice: 18.50,
        leadTime: 21,
        minimumOrderQty: 5,
        lastUpdated: '2025-03-01T00:00:00Z',
        isPreferred: true,
      }
    ],
    priceHistory: [
      { id: 9, date: '2025-01-01T00:00:00Z', price: 18.00, vendorId: 1, changedBy: 1 },
      { id: 10, date: '2025-03-01T00:00:00Z', price: 18.50, vendorId: 1, changedBy: 1 }
    ],
    lastPurchasePrice: 18.50,
    isActive: true,
    createdAt: '2025-01-01T00:00:00Z',
    updatedAt: '2025-03-01T00:00:00Z',
    createdBy: 1,
    specifications: {
      bandwidth: '100MHz',
      attenuation: '10:1',
      input: 'BNC',
      cable: '1.2m',
      compensation: 'Adjustable',
      manufacturer: 'Tektronix'
    }
  }
];

// Mock Purchase Orders
const mockPurchaseOrders: PurchaseOrder[] = [
  {
    id: 1,
    poNumber: 'PO-2025-001',
    vendorId: 1,
    items: [
      {
        id: 1,
        skuId: 1,
        quantity: 100, // Total quantity ordered
        unitPrice: 500,
        totalPrice: 50000.00,
        receivedQuantity: 80, // 80 units received (50 + 30 from invoices)
        acceptedQuantity: 80,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 50000.00,
    status: 'PARTLY_RECEIVED',
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-01-25T00:00:00Z',
      actualDate: null,
      address: '123 Company Street, City, State 12345',
      instructions: 'Deliver to Electronics Warehouse'
    },
    createdBy: 4,
    createdAt: '2025-01-20T09:00:00Z',
    updatedAt: '2025-02-01T16:00:00Z',
    notes: 'Partial delivery received - 80/100 units delivered',
    sentToVendor: true,
    sentToVendorDate: '2025-01-22T10:00:00Z'
  },
  {
    id: 2,
    poNumber: 'PO-2025-002',
    vendorId: 2,
    items: [
      {
        id: 3,
        skuId: 2,
        quantity: 75, // Total quantity ordered
        unitPrice: 600,
        totalPrice: 45000.00,
        receivedQuantity: 75, // All units received
        acceptedQuantity: 75,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 45000.00,
    status: 'FULLY_RECEIVED',
    priority: 'High',
    deliveryDetails: {
      expectedDate: '2025-02-05T00:00:00Z',
      actualDate: '2025-01-25T10:00:00Z',
      address: '123 Company Street, City, State 12345',
      instructions: 'Handle with care - electrical components'
    },
    createdBy: 5,
    createdAt: '2025-01-21T11:15:00Z',
    updatedAt: '2025-01-25T10:00:00Z',
    notes: 'Complete delivery received - 75/75 units delivered',
    sentToVendor: true,
    sentToVendorDate: '2025-01-22T14:30:00Z'
  },
  {
    id: 3,
    poNumber: 'PO-2025-003',
    vendorId: 4,
    items: [
      {
        id: 5,
        skuId: 8,
        quantity: 10,
        unitPrice: 1795.00,
        totalPrice: 17950.00,
        receivedQuantity: 10,
        acceptedQuantity: 9,
        rejectedQuantity: 1
      },
      {
        id: 6,
        skuId: 15,
        quantity: 200,
        unitPrice: 208.00,
        totalPrice: 41600.00,
        receivedQuantity: 200,
        acceptedQuantity: 198,
        rejectedQuantity: 2
      }
    ],
    totalAmount: 59550.00,
    status: 'PARTLY_RECEIVED',
    priority: 'Low',
    deliveryDetails: {
      expectedDate: '2025-02-10T00:00:00Z',
      actualDate: '2025-02-08T10:15:00Z',
      address: '123 Company Street, City, State 12345',
      instructions: 'Deliver to Electrical Store'
    },
    createdBy: 4,
    createdAt: '2025-01-19T14:20:00Z',
    updatedAt: '2025-02-08T16:30:00Z',
    sentToVendor: true,
    sentToVendorDate: '2025-01-20T09:45:00Z'
  },
  {
    id: 4,
    poNumber: 'PO-2025-004',
    vendorId: 3,
    items: [
      {
        id: 7,
        skuId: 4,
        quantity: 80, // Total quantity ordered
        unitPrice: 500,
        totalPrice: 40000.00,
        receivedQuantity: 64, // 64 units received (partial)
        acceptedQuantity: 64,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 40000.00,
    status: 'PARTLY_RECEIVED',
    priority: 'High',
    deliveryDetails: {
      expectedDate: '2025-02-15T00:00:00Z',
      actualDate: null,
      address: '456 Quality Control Ave, City, State 12345',
      instructions: 'Deliver to IQC Testing Area'
    },
    createdBy: 2,
    createdAt: '2025-02-10T09:15:00Z',
    updatedAt: '2025-02-06T15:00:00Z',
    notes: 'Partial delivery received - 64/80 units delivered',
    sentToVendor: true,
    sentToVendorDate: '2025-02-11T11:20:00Z'
  },
  {
    id: 5,
    poNumber: 'PO-2025-005',
    vendorId: 1,
    items: [
      {
        id: 9,
        skuId: 3,
        quantity: 1000,
        unitPrice: 0.08,
        totalPrice: 80.00,
        receivedQuantity: 1000,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      },
      {
        id: 10,
        skuId: 4,
        quantity: 100,
        unitPrice: 3.15,
        totalPrice: 315.00,
        receivedQuantity: 100,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 395.00,
    status: 'APPROVED',
    priority: 'High',
    sentToVendor: true,
    sentToVendorDate: '2025-01-20T10:00:00Z',
    deliveryDetails: {
      expectedDate: '2025-09-25T00:00:00Z',
      actualDate: '2025-09-24T14:30:00Z',
      address: '789 Quality Control Ave, City, State 12345',
      instructions: 'Deliver to IQC Testing Area'
    },
    securityVerified: false,
    createdBy: 2,
    createdAt: '2025-09-23T09:15:00Z',
    updatedAt: '2025-09-23T15:00:00Z',
    notes: 'Ready for security verification - multiple products to verify'
  },
  {
    id: 6,
    poNumber: 'PO-2025-006',
    vendorId: 2,
    items: [
      {
        id: 11,
        skuId: 4,
        quantity: 50,
        unitPrice: 9.75,
        totalPrice: 487.50,
        receivedQuantity: 50,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 487.50,
    status: 'PARTLY_RECEIVED',
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-09-27T00:00:00Z',
      actualDate: '2025-09-26T10:15:00Z',
      address: '456 Electronic Components St, City, State 12345',
      instructions: 'Deliver to Electronics Department'
    },
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-09-26T11:00:00Z',
    createdBy: 3,
    createdAt: '2025-09-23T11:30:00Z',
    updatedAt: '2025-09-24T14:20:00Z',
    notes: 'Microcontroller order for new project development'
  },
  {
    id: 7,
    poNumber: 'PO-2025-007',
    vendorId: 1,
    items: [
      {
        id: 12,
        skuId: 3,
        quantity: 1000,
        unitPrice: 0.06,
        totalPrice: 60.00,
        receivedQuantity: 1000,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 60.00,
    status: 'PARTLY_RECEIVED',
    priority: 'Low',
    deliveryDetails: {
      expectedDate: '2025-09-25T00:00:00Z',
      actualDate: '2025-09-24T16:45:00Z',
      address: '123 TechFlow Ave, City, State 12345',
      instructions: 'Deliver to LED Assembly Area'
    },
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-09-24T17:00:00Z',
    createdBy: 4,
    createdAt: '2025-09-23T14:45:00Z',
    updatedAt: '2025-09-24T16:50:00Z',
    notes: 'LED components for display board project'
  },
  {
    id: 8,
    poNumber: 'PO-2025-008',
    vendorId: 1,
    items: [
      {
        id: 11,
        skuId: 1,
        quantity: 1000,
        unitPrice: 3.50,
        totalPrice: 3500.00,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 3500.00,
    status: 'APPROVED',
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-02-15T00:00:00Z',
      address: '123 Company Street, City, State 12345',
      instructions: 'Standard delivery'
    },
    createdBy: 1,
    createdAt: '2025-01-22T09:00:00Z',
    updatedAt: '2025-01-23T14:30:00Z',
    notes: 'Approved for production line',
    approvedBy: 2,
    approvedAt: '2025-01-23T14:30:00Z',
    approvalHistory: [
      {
        id: 5,
        action: 'created',
        performedBy: 1,
        performedAt: '2025-01-22T09:00:00Z'
      },
      {
        id: 6,
        action: 'approved',
        performedBy: 2,
        performedAt: '2025-01-23T14:30:00Z'
      }
    ]
  },
  {
    id: 9,
    poNumber: 'PO-2025-009',
    vendorId: 3,
    items: [
      {
        id: 12,
        skuId: 3,
        quantity: 150,
        unitPrice: 25.00,
        totalPrice: 3750.00,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 3750.00,
    status: 'NEEDS_REVISION',
    priority: 'Low',
    deliveryDetails: {
      expectedDate: '2025-02-20T00:00:00Z',
      address: '789 Manufacturing Blvd, City, State 12345',
      instructions: 'Delivery during business hours only'
    },
    createdBy: 1,
    createdAt: '2025-01-24T11:00:00Z',
    updatedAt: '2025-01-25T16:45:00Z',
    notes: 'Capacitor order for testing',
    needsRevision: true,
    approvalRemarks: 'Please reduce quantity to 100 units and provide technical specifications.',
    approvalHistory: [
      {
        id: 7,
        action: 'created',
        performedBy: 1,
        performedAt: '2025-01-24T11:00:00Z'
      },
      {
        id: 8,
        action: 'requested_changes',
        performedBy: 2,
        performedAt: '2025-01-25T16:45:00Z',
        remarks: 'Please reduce quantity to 100 units and provide technical specifications.'
      }
    ]
  },
  {
    id: 10,
    poNumber: 'PO-2025-012',
    vendorId: 1,
    items: [
      {
        id: 13,
        skuId: 1,
        quantity: 1000,
        unitPrice: 3.50,
        totalPrice: 3500.00,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 3500.00,
    status: 'CREATED',
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-02-20T00:00:00Z',
      address: '123 Company Street, City, State 12345',
      instructions: 'Standard delivery'
    },
    createdBy: 1,
    createdAt: '2025-01-30T09:00:00Z',
    updatedAt: '2025-01-30T09:00:00Z',
    notes: 'Ready for approval',
    approvalHistory: [
      {
        id: 9,
        action: 'created',
        performedBy: 1,
        performedAt: '2025-01-30T09:00:00Z'
      }
    ]
  },
  {
    id: 11,
    poNumber: 'PO-2025-010',
    vendorId: 2,
    items: [
      {
        id: 14,
        skuId: 2,
        quantity: 500,
        unitPrice: 12.50,
        totalPrice: 6250.00,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 6250.00,
    status: 'NEEDS_REVISION',
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-02-25T00:00:00Z',
      address: '456 Industrial Ave, City, State 12345',
      instructions: 'Standard delivery'
    },
    createdBy: 1,
    createdAt: '2025-01-28T10:00:00Z',
    updatedAt: '2025-01-29T14:30:00Z',
    notes: 'Changes requested by manager',
    needsRevision: true,
    approvalRemarks: 'Please update the delivery address and reduce quantity to 400 units.',
    approvalHistory: [
      {
        id: 10,
        action: 'created',
        performedBy: 1,
        performedAt: '2025-01-28T10:00:00Z'
      },
      {
        id: 11,
        action: 'requested_changes',
        performedBy: 2,
        performedAt: '2025-01-29T14:30:00Z',
        remarks: 'Please update the delivery address and reduce quantity to 400 units.'
      }
    ]
  },
  // Test POs for quality control workflow
  {
    id: 6,
    poNumber: 'PO-2025-006',
    vendorId: 1,
    items: [
      {
        id: 15,
        skuId: 2,
        quantity: 50,
        unitPrice: 300.00,
        totalPrice: 15000.00,
        receivedQuantity: 50,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 15000.00,
    status: 'CREATED', // Ready for quality control
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-02-05T00:00:00Z',
      address: '123 Company Street, City, State 12345',
      instructions: 'Deliver to Electronics Warehouse'
    },
    createdBy: 1,
    createdAt: '2025-01-31T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Ready for quality control testing',
    sentToVendor: true,
    sentToVendorDate: '2025-01-31T10:00:00Z'
  },
  {
    id: 7,
    poNumber: 'PO-2025-007',
    vendorId: 2,
    items: [
      {
        id: 16,
        skuId: 3,
        quantity: 100,
        unitPrice: 200.00,
        totalPrice: 20000.00,
        receivedQuantity: 100,
        acceptedQuantity: 0,
        rejectedQuantity: 0
      }
    ],
    totalAmount: 20000.00,
    status: 'CREATED', // Ready for quality control
    priority: 'Medium',
    deliveryDetails: {
      expectedDate: '2025-02-05T00:00:00Z',
      address: '456 Industrial Ave, City, State 12345',
      instructions: 'Deliver to Electronics Warehouse'
    },
    createdBy: 1,
    createdAt: '2025-01-31T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Ready for quality control testing',
    sentToVendor: true,
    sentToVendorDate: '2025-01-31T10:00:00Z'
  },

  // Additional Purchase Orders for comprehensive workflow demonstration
  {
    id: 11,
    poNumber: 'PO-2025-011',
    vendorId: 2,
    items: [
      {
        id: 17,
        skuId: 5,
        quantity: 75,
        unitPrice: 600,
        totalPrice: 45000,
        receivedQuantity: 75,
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        condition: 'Good',
        notes: 'Control boards for electrical systems'
      }
    ],
    totalAmount: 45000,
    status: 'FULLY_RECEIVED',
    priority: 'High',
    expectedDeliveryDate: '2025-02-26T00:00:00Z',
    actualDeliveryDate: '2025-01-27T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-27T10:00:00Z',
    deliveryAddress: {
      street: '123 Industrial Park',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400001',
      country: 'India',
      instructions: 'Deliver to Quality Control Area'
    },
    createdBy: 1,
    createdAt: '2025-01-27T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Control board order for automation project',
    sentToVendor: true,
    sentToVendorDate: '2025-01-27T09:00:00Z'
  },
  {
    id: 12,
    poNumber: 'PO-2025-012',
    vendorId: 1,
    items: [
      {
        id: 18,
        skuId: 6,
        quantity: 30,
        unitPrice: 500,
        totalPrice: 15000,
        receivedQuantity: 30,
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        condition: 'Good',
        notes: 'Electrical control panels'
      }
    ],
    totalAmount: 15000,
    status: 'FULLY_RECEIVED',
    priority: 'Medium',
    expectedDeliveryDate: '2025-02-27T00:00:00Z',
    actualDeliveryDate: '2025-01-28T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-28T11:00:00Z',
    deliveryAddress: {
      street: '456 Manufacturing Zone',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400002',
      country: 'India',
      instructions: 'Deliver to Warehouse A'
    },
    createdBy: 1,
    createdAt: '2025-01-28T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Electrical panel order for control systems',
    sentToVendor: true,
    sentToVendorDate: '2025-01-28T08:30:00Z'
  },
  {
    id: 13,
    poNumber: 'PO-2025-013',
    vendorId: 3,
    items: [
      {
        id: 19,
        skuId: 7,
        quantity: 100,
        unitPrice: 250,
        totalPrice: 25000,
        receivedQuantity: 85,
        acceptedQuantity: 0,
        rejectedQuantity: 15,
        condition: 'Partial',
        notes: 'LED bulbs - some below standard'
      }
    ],
    totalAmount: 25000,
    status: 'PARTLY_RECEIVED',
    priority: 'Medium',
    expectedDeliveryDate: '2025-02-25T00:00:00Z',
    actualDeliveryDate: '2025-01-26T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-26T14:00:00Z',
    deliveryAddress: {
      street: '789 Lighting District',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400003',
      country: 'India',
      instructions: 'Deliver to Lighting Section'
    },
    createdBy: 1,
    createdAt: '2025-01-26T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'LED bulbs order for lighting upgrade',
    sentToVendor: true,
    sentToVendorDate: '2025-01-26T07:00:00Z'
  },
  {
    id: 14,
    poNumber: 'PO-2025-014',
    vendorId: 2,
    items: [
      {
        id: 20,
        skuId: 8,
        quantity: 70,
        unitPrice: 250,
        totalPrice: 17500,
        receivedQuantity: 60,
        acceptedQuantity: 0,
        rejectedQuantity: 10,
        condition: 'Partial',
        notes: 'Motion sensors - some need calibration'
      }
    ],
    totalAmount: 17500,
    status: 'PARTLY_RECEIVED',
    priority: 'Medium',
    expectedDeliveryDate: '2025-02-24T00:00:00Z',
    actualDeliveryDate: '2025-01-25T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-25T16:00:00Z',
    deliveryAddress: {
      street: '321 Automation Hub',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400004',
      country: 'India',
      instructions: 'Deliver to Automation Lab'
    },
    createdBy: 1,
    createdAt: '2025-01-25T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Motion sensors for automation project',
    sentToVendor: true,
    sentToVendorDate: '2025-01-25T06:30:00Z'
  },
  {
    id: 15,
    poNumber: 'PO-2025-015',
    vendorId: 1,
    items: [
      {
        id: 21,
        skuId: 9,
        quantity: 80,
        unitPrice: 400,
        totalPrice: 32000,
        receivedQuantity: 80,
        acceptedQuantity: 80,
        rejectedQuantity: 0,
        condition: 'Good',
        notes: 'High-grade copper electrical wires'
      }
    ],
    totalAmount: 32000,
    status: 'FULLY_RECEIVED',
    priority: 'High',
    expectedDeliveryDate: '2025-02-23T00:00:00Z',
    actualDeliveryDate: '2025-01-24T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-24T12:00:00Z',
    deliveryAddress: {
      street: '654 Electrical Works',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400005',
      country: 'India',
      instructions: 'Deliver to Electrical Workshop'
    },
    createdBy: 1,
    createdAt: '2025-01-24T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Copper wires for electrical installation',
    sentToVendor: true,
    sentToVendorDate: '2025-01-24T05:00:00Z'
  },
  {
    id: 16,
    poNumber: 'PO-2025-016',
    vendorId: 3,
    items: [
      {
        id: 22,
        skuId: 10,
        quantity: 45,
        unitPrice: 400,
        totalPrice: 18000,
        receivedQuantity: 45,
        acceptedQuantity: 45,
        rejectedQuantity: 0,
        condition: 'Good',
        notes: 'WiFi-enabled smart switches'
      }
    ],
    totalAmount: 18000,
    status: 'FULLY_RECEIVED',
    priority: 'Medium',
    expectedDeliveryDate: '2025-02-22T00:00:00Z',
    actualDeliveryDate: '2025-01-23T00:00:00Z',
    securityVerified: true,
    securityVerifiedBy: 5,
    securityVerifiedAt: '2025-01-23T15:00:00Z',
    deliveryAddress: {
      street: '987 Smart Home Center',
      city: 'Mumbai',
      state: 'Maharashtra',
      zipCode: '400006',
      country: 'India',
      instructions: 'Deliver to Smart Home Section'
    },
    createdBy: 1,
    createdAt: '2025-01-23T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    notes: 'Smart switches for home automation',
    sentToVendor: true,
    sentToVendorDate: '2025-01-23T04:00:00Z'
  }
];

// Mock Quality Inspections
const mockQualityInspections: QualityInspection[] = [
  {
    id: 1,
    poId: 1,
    inspectorId: 3,
    inspectionDate: '2025-01-25T00:00:00Z',
    items: [
      {
        id: 1,
        skuId: 1,
        receivedQty: 5000,
        inspectedQty: 5000,
        acceptedQty: 4950,
        rejectedQty: 50,
        rejectionReasons: ['Resistance value out of tolerance', 'Package damage'],
        qualityRating: 4.0,
        testResults: {
          resistanceTest: 'Pass',
          toleranceCheck: 'Marginal',
          visualInspection: 'Marginal'
        }
      },
      {
        id: 2,
        skuId: 4,
        receivedQty: 100,
        inspectedQty: 100,
        acceptedQty: 100,
        rejectedQty: 0,
        rejectionReasons: [],
        qualityRating: 4.8,
        testResults: {
          functionalTest: 'Pass',
          programmingTest: 'Pass',
          packagingCheck: 'Pass'
        }
      }
    ],
    overallStatus: 'Partly Accepted',
    notes: 'Most electronic components meet quality standards. Some resistors have tolerance issues.',
    photos: ['inspection_1_1.jpg', 'defect_1_1.jpg'],
    certificateRequired: true,
    certificateReceived: true,
    reportGenerated: true
  },
  {
    id: 2,
    poId: 3,
    inspectorId: 3,
    inspectionDate: '2025-02-08T00:00:00Z',
    items: [
      {
        id: 3,
        skuId: 8,
        receivedQty: 10,
        inspectedQty: 10,
        acceptedQty: 9,
        rejectedQty: 1,
        rejectionReasons: ['Relay contact failure'],
        qualityRating: 3.8,
        testResults: {
          relayTest: 'Marginal',
          continuityTest: 'Pass',
          insulationTest: 'Pass'
        }
      },
      {
        id: 4,
        skuId: 15,
        receivedQty: 200,
        inspectedQty: 200,
        acceptedQty: 198,
        rejectedQty: 2,
        rejectionReasons: ['Ceramic body cracks'],
        qualityRating: 4.2,
        testResults: {
          continuityTest: 'Pass',
          ratingTest: 'Pass',
          visualInspection: 'Marginal'
        }
      }
    ],
    overallStatus: 'Partly Accepted',
    notes: 'One relay module failed contact testing. Minor ceramic body cracks found on 2 fuses.',
    photos: ['inspection_2_1.jpg'],
    certificateRequired: false,
    certificateReceived: false,
    reportGenerated: true
  }
];

// Mock Invoices
const mockInvoices: Invoice[] = [
  {
    id: 1,
    invoiceNumber: 'INV-2025-001',
    poId: 1,
    poNumber: 'PO-2025-001',
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poAmount: 50000.00,
    amount: 36000.00,
    subtotal: 36000.00,
    taxAmount: 2880.00,
    totalAmount: 38880.00,
    issueDate: '2025-01-26T00:00:00Z',
    invoiceDate: '2025-01-26T00:00:00Z',
    dueDate: '2025-02-25T00:00:00Z',
    receivedStatus: 'Normal',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 100,
    pendingQuantity: 0,
    status: 'Created',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    paymentMethod: 'Bank Transfer',
    paymentDate: '2025-02-20T00:00:00Z',
    paymentReference: 'TXN-20240220-001',
    items: [
      {
        id: 1,
        skuId: 1,
        skuCode: 'LED-9W-001',
        skuName: 'LED Bulbs 9W',
        quantity: 50,
        unitPrice: 720.00,
        totalPrice: 36000.00,
        description: 'LED Bulbs 9W'
      }
    ],
    notes: 'Payment completed on time',
    attachments: ['invoice_001.pdf', 'delivery_receipt_001.pdf'],
    createdAt: '2025-01-26T00:00:00Z',
    updatedAt: '2025-02-20T00:00:00Z',
    // Delivery status fields
    isReceived: true,
    receivedAt: '2025-01-27T10:30:00Z',
    deliveryStatus: 'Received',
    deliveredBy: 2,
    deliveryNotes: 'Delivered to manager office',
    // Manager Section fields
    handedOverAt: '2025-01-27T09:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager John',
    receivedBy: 1,
    receivedByName: 'Manager Smith'
  },
  {
    id: 2,
    invoiceNumber: 'INV-2025-002',
    poId: 3,
    poNumber: 'PO-2025-003',
    vendorId: 4,
    vendorName: 'PowerTech Solutions',
    poAmount: 75000.00,
    amount: 57734.00,
    subtotal: 57734.00,
    taxAmount: 4618.72,
    totalAmount: 62352.72,
    receivedStatus: 'Normal',
    poQuantity: 50,
    invoicedQuantity: 50,
    receivedQuantity: 50,
    pendingQuantity: 0,
    issueDate: '2025-02-09T00:00:00Z',
    invoiceDate: '2025-02-09T00:00:00Z',
    dueDate: '2025-02-24T00:00:00Z',
    status: 'Approved',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 15',
    items: [
      {
        id: 2,
        skuId: 3,
        skuCode: 'CB-20A-001',
        skuName: 'Circuit Breakers 20A',
        quantity: 25,
        unitPrice: 2309.36,
        totalPrice: 57734.00,
        description: 'Circuit Breakers 20A'
      }
    ],
    notes: 'Adjusted for rejected items',
    attachments: ['invoice_002.pdf'],
    createdAt: '2025-02-09T00:00:00Z',
    updatedAt: '2025-02-09T00:00:00Z',
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    deliveryNotes: 'Pending delivery to manager',
    // Manager Section fields
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },
  {
    id: 3,
    invoiceNumber: 'INV-2025-003',
    poId: 2,
    poNumber: 'PO-2025-002',
    vendorId: 2,
    vendorName: 'TechCorp Solutions',
    poAmount: 25000.00,
    amount: 25000.00,
    subtotal: 25000.00,
    taxAmount: 2000.00,
    totalAmount: 27000.00,
    receivedStatus: 'Received',
    poQuantity: 200,
    invoicedQuantity: 200,
    receivedQuantity: 200,
    pendingQuantity: 0,
    issueDate: '2025-01-28T00:00:00Z',
    invoiceDate: '2025-01-28T00:00:00Z',
    dueDate: '2025-02-27T00:00:00Z',
    status: 'Created', // Ready for quality control
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 3,
        skuId: 2,
        skuCode: 'SW-16A-001',
        skuName: 'Switches 16A',
        quantity: 100,
        unitPrice: 250.00,
        totalPrice: 25000.00,
        description: 'Switches 16A'
      }
    ],
    notes: 'IQC testing completed - Rating: 4.5/5',
    attachments: ['invoice_003.pdf'],
    createdAt: '2025-01-28T00:00:00Z',
    updatedAt: '2025-01-28T12:00:00Z',
    // IQC Testing fields
    qualityTested: true,
    qualityTestedBy: 1,
    qualityTestedAt: '2025-01-28T12:00:00Z',
    qualityRating: 4.5,
    qualityNotes: 'Good quality, minor defects in packaging',
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },
  {
    id: 4,
    invoiceNumber: 'INV-2025-004',
    poId: 4,
    poNumber: 'PO-2025-004',
    vendorId: 3,
    vendorName: 'GreenEnergy Systems',
    poAmount: 40000.00,
    amount: 40000.00,
    subtotal: 40000.00,
    taxAmount: 3200.00,
    totalAmount: 43200.00,
    receivedStatus: 'Not Received',
    poQuantity: 150,
    invoicedQuantity: 150,
    receivedQuantity: 120,
    pendingQuantity: 30,
    issueDate: '2025-01-29T00:00:00Z',
    invoiceDate: '2025-01-29T00:00:00Z',
    dueDate: '2025-02-28T00:00:00Z',
    status: 'Handed Over', // Handed over to Floor Manager
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 4,
        skuId: 4,
        skuCode: 'CBL-2.5MM-001',
        skuName: 'Cables 2.5mm',
        quantity: 150,
        unitPrice: 266.67,
        totalPrice: 40000.00,
        description: 'Cables 2.5mm'
      }
    ],
    notes: 'IQC testing completed - Rating: 3.2/5',
    attachments: ['invoice_004.pdf'],
    createdAt: '2025-01-29T00:00:00Z',
    updatedAt: '2025-01-29T15:30:00Z',
    // IQC Testing fields
    qualityTested: true,
    qualityTestedBy: 1,
    qualityTestedAt: '2025-01-29T15:30:00Z',
    qualityRating: 3.2,
    qualityNotes: 'Acceptable quality, some packaging issues',
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: '2025-01-29T16:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Sarah',
    receivedBy: undefined,
    receivedByName: undefined
  },
  {
    id: 5,
    invoiceNumber: 'INV-2025-005',
    poId: 5,
    poNumber: 'PO-2025-005',
    vendorId: 5,
    vendorName: 'SmartHome Devices',
    poAmount: 30000.00,
    amount: 30000.00,
    subtotal: 30000.00,
    taxAmount: 2400.00,
    totalAmount: 32400.00,
    receivedStatus: 'Received',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 100,
    pendingQuantity: 0,
    issueDate: '2025-01-30T00:00:00Z',
    invoiceDate: '2025-01-30T00:00:00Z',
    dueDate: '2025-03-01T00:00:00Z',
    status: 'Complete', // Fully processed
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 5,
        skuId: 5,
        skuCode: 'SENS-MOTION-001',
        skuName: 'Motion Sensors',
        quantity: 100,
        unitPrice: 300.00,
        totalPrice: 30000.00,
        description: 'Motion Sensors'
      }
    ],
    notes: 'IQC testing completed - Rating: 4.8/5',
    attachments: ['invoice_005.pdf'],
    createdAt: '2025-01-30T00:00:00Z',
    updatedAt: '2025-01-30T16:45:00Z',
    // IQC Testing fields
    qualityTested: true,
    qualityTestedBy: 1,
    qualityTestedAt: '2025-01-30T14:00:00Z',
    qualityRating: 4.8,
    qualityNotes: 'Excellent quality, no defects found',
    // Quantity Testing fields
    quantityTested: true,
    quantityTestedBy: 2,
    quantityTestedAt: '2025-01-30T16:00:00Z',
    quantityTestResults: {
      totalPOQuantity: 100,
      totalReceivedQuantity: 100,
      totalAcceptedQuantity: 100,
      totalRejectedQuantity: 0
    },
    // Delivery status fields
    isReceived: true,
    receivedAt: '2025-01-30T16:45:00Z',
    deliveryStatus: 'Received',
    deliveredBy: 2,
    deliveryNotes: 'Delivered to manager office',
    // Manager Section fields
    handedOverAt: '2025-01-30T15:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Mike',
    receivedBy: 1,
    receivedByName: 'Manager Johnson'
  },
  // Test invoices for quality control workflow
  {
    id: 6,
    invoiceNumber: 'INV-2025-006',
    poId: 6,
    poNumber: 'PO-2025-006',
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poAmount: 15000.00,
    amount: 15000.00,
    subtotal: 15000.00,
    taxAmount: 1200.00,
    totalAmount: 16200.00,
    receivedStatus: 'Normal',
    poQuantity: 50,
    invoicedQuantity: 50,
    receivedQuantity: 50,
    pendingQuantity: 0,
    issueDate: '2025-01-31T00:00:00Z',
    invoiceDate: '2025-01-31T00:00:00Z',
    dueDate: '2025-03-02T00:00:00Z',
    status: 'Created', // Ready for quality control
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 6,
        skuId: 2,
        skuCode: 'SWITCH-DP-001',
        skuName: 'Double Pole Switches',
        quantity: 50,
        unitPrice: 300.00,
        totalPrice: 15000.00,
        description: 'Double Pole Switches 20A'
      }
    ],
    notes: 'Ready for quality control testing',
    attachments: ['invoice_006.pdf'],
    createdAt: '2025-01-31T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    // Quality testing fields - not tested yet
    qualityTested: false,
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },
  {
    id: 7,
    invoiceNumber: 'INV-2025-007',
    poId: 7,
    poNumber: 'PO-2025-007',
    vendorId: 2,
    vendorName: 'TechCorp Solutions',
    poAmount: 20000.00,
    amount: 20000.00,
    subtotal: 20000.00,
    taxAmount: 1600.00,
    totalAmount: 21600.00,
    receivedStatus: 'Normal',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 100,
    pendingQuantity: 0,
    issueDate: '2025-01-31T00:00:00Z',
    invoiceDate: '2025-01-31T00:00:00Z',
    dueDate: '2025-03-02T00:00:00Z',
    status: 'Created', // Ready for quality control
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 7,
        skuId: 3,
        skuCode: 'OUTLET-13A-001',
        skuName: '13A Power Outlets',
        quantity: 100,
        unitPrice: 200.00,
        totalPrice: 20000.00,
        description: '13A Power Outlets with Earth'
      }
    ],
    notes: 'Ready for quality control testing',
    attachments: ['invoice_007.pdf'],
    createdAt: '2025-01-31T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    // Quality testing fields - not tested yet
    qualityTested: false,
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },

  // Additional mock invoices for different sections
  {
    id: 8,
    invoiceNumber: 'INV-2025-008',
    poId: 8,
    poNumber: 'PO-2025-008',
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poAmount: 25000,
    amount: 25000,
    subtotal: 22500,
    taxAmount: 2500,
    totalAmount: 25000,
    receivedStatus: 'Received',
    poQuantity: 50,
    invoicedQuantity: 50,
    receivedQuantity: 50,
    pendingQuantity: 0,
    issueDate: '2025-01-30T00:00:00Z',
    invoiceDate: '2025-01-30T00:00:00Z',
    dueDate: '2025-03-01T00:00:00Z',
    status: 'Fully Accepted', // Ready for Floor Manager Dispatch
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 8,
        skuId: 1,
        skuCode: 'CABLE-001',
        skuName: 'Electrical Cables',
        quantity: 50,
        unitPrice: 500,
        totalPrice: 25000,
        description: 'High quality electrical cables'
      }
    ],
    notes: 'Quality tested and approved',
    attachments: ['invoice_008.pdf'],
    createdAt: '2025-01-30T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    // Quality testing fields - tested
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T10:00:00Z',
    qualityRating: 4.5,
    qualityNotes: 'Good quality, minor packaging issues',
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: '2025-01-31T11:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager John',
    receivedBy: undefined,
    receivedByName: undefined
  },

  {
    id: 9,
    invoiceNumber: 'INV-2025-009',
    poId: 9,
    poNumber: 'PO-2025-009',
    vendorId: 2,
    vendorName: 'PowerTech Solutions',
    poAmount: 15000,
    amount: 15000,
    subtotal: 13500,
    taxAmount: 1500,
    totalAmount: 15000,
    receivedStatus: 'Not Received',
    poQuantity: 30,
    invoicedQuantity: 30,
    receivedQuantity: 25,
    pendingQuantity: 5,
    issueDate: '2025-01-29T00:00:00Z',
    invoiceDate: '2025-01-29T00:00:00Z',
    dueDate: '2025-02-28T00:00:00Z',
    status: 'Partly Accepted', // Ready for Floor Manager Dispatch
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 9,
        skuId: 2,
        skuCode: 'SWITCH-001',
        skuName: 'Electrical Switches',
        quantity: 30,
        unitPrice: 500,
        totalPrice: 15000,
        description: 'Standard electrical switches'
      }
    ],
    notes: 'Partially approved - some items need rework',
    attachments: ['invoice_009.pdf'],
    createdAt: '2025-01-29T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    // Quality testing fields - tested
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T09:00:00Z',
    qualityRating: 3.2,
    qualityNotes: 'Some items below standard, partial acceptance',
    // Delivery status fields
    isReceived: false,
    deliveryStatus: 'Not Received',
    // Manager Section fields
    handedOverAt: '2025-01-31T12:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Sarah',
    receivedBy: undefined,
    receivedByName: undefined
  },

  {
    id: 10,
    invoiceNumber: 'INV-2025-010',
    poId: 10,
    poNumber: 'PO-2025-010',
    vendorId: 3,
    vendorName: 'TechComponents Ltd.',
    poAmount: 30000,
    amount: 30000,
    subtotal: 27000,
    taxAmount: 3000,
    totalAmount: 30000,
    receivedStatus: 'Received',
    poQuantity: 60,
    invoicedQuantity: 60,
    receivedQuantity: 60,
    pendingQuantity: 0,
    issueDate: '2025-01-28T00:00:00Z',
    invoiceDate: '2025-01-28T00:00:00Z',
    dueDate: '2025-02-27T00:00:00Z',
    status: 'Complete', // Completed invoice
    paymentStatus: 'Paid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 10,
        skuId: 4,
        skuCode: 'LIGHT-001',
        skuName: 'LED Lights',
        quantity: 60,
        unitPrice: 500,
        totalPrice: 30000,
        description: 'Energy efficient LED lights'
      }
    ],
    notes: 'Fully completed and paid',
    attachments: ['invoice_010.pdf'],
    createdAt: '2025-01-28T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    // Quality testing fields - tested
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-30T14:00:00Z',
    qualityRating: 4.8,
    qualityNotes: 'Excellent quality, fully approved',
    // Delivery status fields
    isReceived: true,
    deliveryStatus: 'Received',
    receivedAt: '2025-01-31T16:00:00Z',
    // Manager Section fields
    handedOverAt: '2025-01-30T15:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Mike',
    receivedBy: 4,
    receivedByName: 'Manager Alex'
  },

  // Additional mock invoices for comprehensive workflow demonstration
  
  // Floor Manager Dispatch Section - Fully Accepted invoices
  {
    id: 11,
    invoiceNumber: 'INV-2025-011',
    poId: 11,
    poNumber: 'PO-2025-011',
    vendorId: 2,
    vendorName: 'Power Systems Solutions',
    poAmount: 45000,
    amount: 45000,
    subtotal: 40500,
    taxAmount: 4500,
    totalAmount: 45000,
    receivedStatus: 'Received',
    poQuantity: 75,
    invoicedQuantity: 75,
    receivedQuantity: 75,
    pendingQuantity: 0,
    issueDate: '2025-01-27T00:00:00Z',
    invoiceDate: '2025-01-27T00:00:00Z',
    dueDate: '2025-02-26T00:00:00Z',
    status: 'Fully Accepted',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 11,
        skuId: 5,
        skuCode: 'CONTROL-001',
        skuName: 'Control Board',
        quantity: 75,
        unitPrice: 600,
        totalPrice: 45000,
        description: 'Advanced control board for electrical systems'
      }
    ],
    notes: 'Quality tested - fully approved',
    attachments: ['invoice_011.pdf'],
    createdAt: '2025-01-27T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T08:00:00Z',
    qualityRating: 4.8,
    qualityNotes: 'Excellent quality control board',
    isReceived: false,
    deliveryStatus: 'Not Received',
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },

  {
    id: 12,
    invoiceNumber: 'INV-2025-012',
    poId: 12,
    poNumber: 'PO-2025-012',
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poAmount: 15000,
    amount: 15000,
    subtotal: 13500,
    taxAmount: 1500,
    totalAmount: 15000,
    receivedStatus: 'Received',
    poQuantity: 30,
    invoicedQuantity: 30,
    receivedQuantity: 30,
    pendingQuantity: 0,
    issueDate: '2025-01-28T00:00:00Z',
    invoiceDate: '2025-01-28T00:00:00Z',
    dueDate: '2025-02-27T00:00:00Z',
    status: 'Fully Accepted',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 12,
        skuId: 6,
        skuCode: 'PANEL-001',
        skuName: 'Electrical Panel',
        quantity: 30,
        unitPrice: 500,
        totalPrice: 15000,
        description: 'Standard electrical control panel'
      }
    ],
    notes: 'Quality tested and approved',
    attachments: ['invoice_012.pdf'],
    createdAt: '2025-01-28T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T09:30:00Z',
    qualityRating: 4.2,
    qualityNotes: 'Good quality panel with minor cosmetic issues',
    isReceived: false,
    deliveryStatus: 'Not Received',
    handedOverAt: undefined,
    handedOverBy: undefined,
    handedOverByName: undefined,
    receivedBy: undefined,
    receivedByName: undefined
  },

  // Quantity Testing Section - Partly Accepted invoices
  {
    id: 13,
    invoiceNumber: 'INV-2025-013',
    poId: 13,
    poNumber: 'PO-2025-013',
    vendorId: 3,
    vendorName: 'TechComponents Ltd.',
    poAmount: 25000,
    amount: 25000,
    subtotal: 22500,
    taxAmount: 2500,
    totalAmount: 25000,
    receivedStatus: 'Not Received',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 85,
    pendingQuantity: 15,
    issueDate: '2025-01-26T00:00:00Z',
    invoiceDate: '2025-01-26T00:00:00Z',
    dueDate: '2025-02-25T00:00:00Z',
    status: 'Partly Accepted',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 13,
        skuId: 7,
        skuCode: 'LED-001',
        skuName: 'LED Bulbs 9W',
        quantity: 100,
        unitPrice: 250,
        totalPrice: 25000,
        description: 'Energy efficient LED bulbs'
      }
    ],
    notes: 'Partially approved - some items need rework',
    attachments: ['invoice_013.pdf'],
    createdAt: '2025-01-26T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T11:00:00Z',
    qualityRating: 3.5,
    qualityNotes: 'Some bulbs below standard, partial acceptance',
    isReceived: false,
    deliveryStatus: 'Not Received',
    handedOverAt: '2025-01-31T12:30:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Sarah',
    receivedBy: undefined,
    receivedByName: undefined
  },

  {
    id: 14,
    invoiceNumber: 'INV-2025-014',
    poId: 14,
    poNumber: 'PO-2025-014',
    vendorId: 2,
    vendorName: 'Power Systems Solutions',
    poAmount: 17500,
    amount: 17500,
    subtotal: 15750,
    taxAmount: 1750,
    totalAmount: 17500,
    receivedStatus: 'Not Received',
    poQuantity: 70,
    invoicedQuantity: 70,
    receivedQuantity: 60,
    pendingQuantity: 10,
    issueDate: '2025-01-25T00:00:00Z',
    invoiceDate: '2025-01-25T00:00:00Z',
    dueDate: '2025-02-24T00:00:00Z',
    status: 'Partly Accepted',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 14,
        skuId: 8,
        skuCode: 'SENSOR-001',
        skuName: 'Motion Sensors',
        quantity: 70,
        unitPrice: 250,
        totalPrice: 17500,
        description: 'PIR motion sensors for automation'
      }
    ],
    notes: 'Partial approval - some sensors need calibration',
    attachments: ['invoice_014.pdf'],
    createdAt: '2025-01-25T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-31T10:15:00Z',
    qualityRating: 3.8,
    qualityNotes: 'Good sensors, some need recalibration',
    isReceived: false,
    deliveryStatus: 'Not Received',
    handedOverAt: '2025-01-31T13:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager John',
    receivedBy: undefined,
    receivedByName: undefined
  },

  // Manager Section - Complete invoices
  {
    id: 15,
    invoiceNumber: 'INV-2025-015',
    poId: 15,
    poNumber: 'PO-2025-015',
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poAmount: 32000,
    amount: 32000,
    subtotal: 28800,
    taxAmount: 3200,
    totalAmount: 32000,
    receivedStatus: 'Received',
    poQuantity: 80,
    invoicedQuantity: 80,
    receivedQuantity: 80,
    pendingQuantity: 0,
    issueDate: '2025-01-24T00:00:00Z',
    invoiceDate: '2025-01-24T00:00:00Z',
    dueDate: '2025-02-23T00:00:00Z',
    status: 'Complete',
    paymentStatus: 'Paid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 15,
        skuId: 9,
        skuCode: 'WIRE-001',
        skuName: 'Copper Wires',
        quantity: 80,
        unitPrice: 400,
        totalPrice: 32000,
        description: 'High-grade copper electrical wires'
      }
    ],
    notes: 'Fully completed and paid',
    attachments: ['invoice_015.pdf'],
    createdAt: '2025-01-24T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-30T14:30:00Z',
    qualityRating: 4.7,
    qualityNotes: 'Excellent copper wire quality',
    isReceived: true,
    deliveryStatus: 'Received',
    receivedAt: '2025-01-31T15:30:00Z',
    handedOverAt: '2025-01-30T16:00:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Mike',
    receivedBy: 4,
    receivedByName: 'Manager Alex'
  },

  {
    id: 16,
    invoiceNumber: 'INV-2025-016',
    poId: 16,
    poNumber: 'PO-2025-016',
    vendorId: 3,
    vendorName: 'TechComponents Ltd.',
    poAmount: 18000,
    amount: 18000,
    subtotal: 16200,
    taxAmount: 1800,
    totalAmount: 18000,
    receivedStatus: 'Received',
    poQuantity: 45,
    invoicedQuantity: 45,
    receivedQuantity: 45,
    pendingQuantity: 0,
    issueDate: '2025-01-23T00:00:00Z',
    invoiceDate: '2025-01-23T00:00:00Z',
    dueDate: '2025-02-22T00:00:00Z',
    status: 'Complete',
    paymentStatus: 'Paid',
    paymentTerms: 'Net 30',
    items: [
      {
        id: 16,
        skuId: 10,
        skuCode: 'SWITCH-002',
        skuName: 'Smart Switches',
        quantity: 45,
        unitPrice: 400,
        totalPrice: 18000,
        description: 'WiFi-enabled smart switches'
      }
    ],
    notes: 'Completed and paid',
    attachments: ['invoice_016.pdf'],
    createdAt: '2025-01-23T00:00:00Z',
    updatedAt: '2025-01-31T00:00:00Z',
    qualityTested: true,
    qualityTestedBy: 2,
    qualityTestedAt: '2025-01-29T13:45:00Z',
    qualityRating: 4.9,
    qualityNotes: 'Perfect smart switches, excellent functionality',
    isReceived: true,
    deliveryStatus: 'Received',
    receivedAt: '2025-01-31T14:15:00Z',
    handedOverAt: '2025-01-29T15:30:00Z',
    handedOverBy: 3,
    handedOverByName: 'Floor Manager Lisa',
    receivedBy: 4,
    receivedByName: 'Manager Alex'
  }
];

// Mock Gate Entries
const mockGateEntries: GateEntry[] = [
  {
    id: 1,
    poId: 1,
    vehicleNumber: 'TRK-123',
    driverName: 'James Wilson',
    driverPhone: '+1-555-9876',
    entryTime: '2025-01-25T14:15:00Z',
    exitTime: '2025-01-25T15:30:00Z',
    securityOfficer: 5,
    items: [
      {
        skuId: 1,
        expectedQuantity: 5000,
        receivedQuantity: 5000,
        condition: 'Good',
        notes: 'All electronic components properly packaged in anti-static bags'
      },
      {
        skuId: 4,
        expectedQuantity: 100,
        receivedQuantity: 100,
        condition: 'Good',
        notes: 'Microcontrollers in original manufacturer packaging'
      }
    ],
    photos: ['gate_entry_1_1.jpg', 'gate_entry_1_2.jpg'],
    notes: 'Driver provided all required documentation and certificates'
  },
  {
    id: 2,
    poId: 3,
    vehicleNumber: 'VAN-456',
    driverName: 'Maria Garcia',
    driverPhone: '+1-555-5432',
    entryTime: '2025-02-08T10:00:00Z',
    exitTime: '2025-02-08T10:45:00Z',
    securityOfficer: 5,
    items: [
      {
        skuId: 8,
        expectedQuantity: 10,
        receivedQuantity: 10,
        condition: 'Good',
        notes: 'Relay modules properly secured in protective packaging'
      },
      {
        skuId: 15,
        expectedQuantity: 200,
        receivedQuantity: 200,
        condition: 'Good',
        notes: 'Fuses in original manufacturer packaging with proper labeling'
      }
    ],
    photos: ['gate_entry_2_1.jpg']
  }
];

// Helper functions for data manipulation
export const getVendorById = (id: number): Vendor | undefined => {
  return mockVendors.find(vendor => vendor.id === id);
};

export const getSKUById = (id: number): SKU | undefined => {
  return mockSKUs.find(sku => sku.id === id);
};

export const getUserById = (id: number): User | undefined => {
  return mockUsers.find(user => user.id === id);
};

export const getPOById = (id: number): PurchaseOrder | undefined => {
  return mockPurchaseOrders.find(po => po.id === id);
};

// Pricing analytics and comparison utilities
export interface VendorPriceComparison {
  vendorId: number;
  vendorName: string;
  currentPrice: number;
  previousPrice?: number;
  priceChange?: number;
  priceChangePercent?: number;
  leadTime: number;
  minimumOrderQty: number;
  lastUpdated: string;
  isPreferred: boolean;
  totalCost: number; // price * quantity
  priceHistory: PriceHistoryEntry[];
  performanceRating: number;
}

export interface PriceTrend {
  skuId: number;
  vendorId: number;
  trend: 'increasing' | 'decreasing' | 'stable';
  trendPercent: number;
  averagePrice: number;
  lowestPrice: number;
  highestPrice: number;
  priceVolatility: number;
}

// Mock Debit Notes data
export const mockDebitNotes: DebitNote[] = [
  {
    id: 1,
    debitNoteNumber: 'DN-2025-001',
    linkedPOId: 1,
    linkedPONumber: 'PO-2025-001',
    vendorId: 1,
    vendorName: 'TechCorp Solutions',
    reason: 'Damaged items during delivery',
    items: [
      {
        id: 1,
        skuId: 1,
        skuName: 'LED Bulb 10W',
        skuCode: 'LED-10W-001',
        quantity: 100,
        unitPrice: 150,
        totalPrice: 15000,
        rejectedQuantity: 15,
        rejectionReason: 'Physical damage - broken glass'
      }
    ],
    totalAmount: 2250,
    status: 'Open',
    createdAt: '2025-01-15T10:30:00Z',
    createdBy: 2,
    createdByName: 'John Smith',
    notes: 'Items were damaged during transit. Vendor to replace or credit account.'
  },
  {
    id: 2,
    debitNoteNumber: 'DN-2025-002',
    linkedInvoiceId: 2,
    linkedInvoiceNumber: 'INV-2025-002',
    vendorId: 2,
    vendorName: 'ElectroSupply Ltd',
    reason: 'Wrong quantity delivered',
    items: [
      {
        id: 2,
        skuId: 3,
        skuName: 'Circuit Breaker 32A',
        skuCode: 'CB-32A-001',
        quantity: 50,
        unitPrice: 800,
        totalPrice: 40000,
        rejectedQuantity: 10,
        rejectionReason: 'Short delivered - only 40 units received'
      }
    ],
    totalAmount: 8000,
    status: 'Sent to Vendor',
    createdAt: '2025-01-18T14:15:00Z',
    createdBy: 3,
    createdByName: 'Sarah Johnson',
    sentToVendorAt: '2025-01-19T09:00:00Z',
    notes: 'Vendor acknowledged the short delivery. Credit note expected.'
  }
];

// Mock Vendor Invoices
export const mockVendorInvoices: VendorInvoice[] = [
  {
    id: 1,
    invoiceNumber: 'VINV-2025-001',
    poId: 1,
    vendorId: 1,
    vendorName: 'ElectroSupply Co.',
    poNumber: 'PO-2025-001',
    items: [
      {
        id: 1,
        skuId: 1,
        skuName: 'LED Bulbs 9W',
        skuCode: 'LED-9W-001',
        quantity: 50,
        unitPrice: 720.00,
        totalPrice: 36000.00,
        deliveredQuantity: 50,
        acceptedQuantity: 48,
        rejectedQuantity: 2,
        qualityNotes: '2 bulbs had packaging damage'
      }
    ],
    subtotal: 36000.00,
    taxAmount: 2880.00,
    totalAmount: 38880.00,
    status: 'paid',
    sentDate: '2025-01-20T00:00:00Z',
    deliveredDate: '2025-01-25T00:00:00Z',
    verifiedDate: '2025-01-26T00:00:00Z',
    paidDate: '2025-02-20T00:00:00Z',
    dueDate: '2025-02-25T00:00:00Z',
    notes: 'Payment completed on time',
    attachments: ['vendor_invoice_001.pdf', 'delivery_receipt_001.pdf'],
    createdAt: '2025-01-20T00:00:00Z',
    updatedAt: '2025-02-20T00:00:00Z'
  },
  {
    id: 2,
    invoiceNumber: 'VINV-2025-002',
    poId: 3,
    vendorId: 4,
    vendorName: 'PowerTech Solutions',
    poNumber: 'PO-2025-003',
    items: [
      {
        id: 2,
        skuId: 3,
        skuName: 'Circuit Breakers 20A',
        skuCode: 'CB-20A-001',
        quantity: 25,
        unitPrice: 2309.36,
        totalPrice: 57734.00,
        deliveredQuantity: 25,
        acceptedQuantity: 25,
        rejectedQuantity: 0,
        qualityNotes: 'All items passed quality inspection'
      }
    ],
    subtotal: 57734.00,
    taxAmount: 4618.72,
    totalAmount: 62352.72,
    status: 'verified',
    sentDate: '2025-02-05T00:00:00Z',
    deliveredDate: '2025-02-08T00:00:00Z',
    verifiedDate: '2025-02-09T00:00:00Z',
    dueDate: '2025-02-24T00:00:00Z',
    notes: 'Ready for payment processing',
    attachments: ['vendor_invoice_002.pdf'],
    createdAt: '2025-02-05T00:00:00Z',
    updatedAt: '2025-02-09T00:00:00Z'
  },
  {
    id: 3,
    invoiceNumber: 'VINV-2025-003',
    poId: 2,
    vendorId: 2,
    vendorName: 'TechComponents Ltd.',
    poNumber: 'PO-2025-002',
    items: [
      {
        id: 3,
        skuId: 2,
        skuName: 'Resistors 1K Ohm',
        skuCode: 'RES-1K-001',
        quantity: 100,
        unitPrice: 15.50,
        totalPrice: 1550.00,
        deliveredQuantity: 100,
        acceptedQuantity: 95,
        rejectedQuantity: 5,
        qualityNotes: '5 resistors failed tolerance test'
      }
    ],
    subtotal: 1550.00,
    taxAmount: 124.00,
    totalAmount: 1674.00,
    status: 'delivered',
    sentDate: '2025-01-15T00:00:00Z',
    deliveredDate: '2025-01-18T00:00:00Z',
    dueDate: '2025-02-15T00:00:00Z',
    notes: 'Awaiting quality verification',
    attachments: ['vendor_invoice_003.pdf'],
    createdAt: '2025-01-15T00:00:00Z',
    updatedAt: '2025-01-18T00:00:00Z'
  }
];

// Mock PO Sent to Vendors
const mockSentPOsToVendors: POSentToVendor[] = [
  {
    id: 1,
    poId: 1,
    vendorId: 1,
    sentDate: '2025-01-15T00:00:00Z',
    sentBy: 1,
    deliveryMethod: 'email',
    status: 'completed',
    acknowledgmentDate: '2025-01-16T00:00:00Z',
    expectedDeliveryDate: '2025-01-25T00:00:00Z',
    notes: 'PO sent via email to procurement@electrosupply.com',
    attachments: ['po_2025_001.pdf'],
    createdAt: '2025-01-15T00:00:00Z',
    updatedAt: '2025-01-25T00:00:00Z'
  },
  {
    id: 2,
    poId: 2,
    vendorId: 2,
    sentDate: '2025-01-10T00:00:00Z',
    sentBy: 1,
    deliveryMethod: 'portal',
    status: 'completed',
    acknowledgmentDate: '2025-01-11T00:00:00Z',
    expectedDeliveryDate: '2025-01-20T00:00:00Z',
    notes: 'PO uploaded to vendor portal',
    attachments: ['po_2025_002.pdf'],
    createdAt: '2025-01-10T00:00:00Z',
    updatedAt: '2025-01-20T00:00:00Z'
  },
  {
    id: 3,
    poId: 3,
    vendorId: 4,
    sentDate: '2025-02-01T00:00:00Z',
    sentBy: 1,
    deliveryMethod: 'email',
    status: 'completed',
    acknowledgmentDate: '2025-02-02T00:00:00Z',
    expectedDeliveryDate: '2025-02-10T00:00:00Z',
    notes: 'PO sent via email to orders@powertech.com',
    attachments: ['po_2025_003.pdf'],
    createdAt: '2025-02-01T00:00:00Z',
    updatedAt: '2025-02-10T00:00:00Z'
  }
];

export const getVendorPriceComparison = (skuId: number, quantity: number): VendorPriceComparison[] => {
  const sku = getSKUById(skuId);
  if (!sku) return [];

  return (sku.linkedVendors || []).map(vendorLink => {
    const vendor = getVendorById(vendorLink.vendorId);
    
    // Clean and validate price history data
    const priceHistory = cleanPriceHistory((sku.priceHistory || []).filter(ph => ph.vendorId === vendorLink.vendorId));
    
    // Validate current price
    const currentPrice = vendorLink.defaultPrice || 0;
    
    // Fix: Better logic for finding previous price with error handling
    let previousPrice: number | undefined = undefined;
    let priceChange = 0;
    let priceChangePercent = 0;
    
    try {
      if (priceHistory.length > 1) {
        // Get the second most recent price (previous price)
        const prevPrice = priceHistory[1].price;
        if (prevPrice && prevPrice > 0) {
          previousPrice = prevPrice;
          priceChange = currentPrice - previousPrice;
          priceChangePercent = (priceChange / previousPrice) * 100;
        }
      } else if (priceHistory.length === 1) {
        // If only one price entry exists, use it as previous price
        const prevPrice = priceHistory[0].price;
        if (prevPrice && prevPrice > 0) {
          previousPrice = prevPrice;
          priceChange = currentPrice - previousPrice;
          priceChangePercent = (priceChange / previousPrice) * 100;
        }
      }
    } catch (error) {
      console.warn(`Error calculating price change for SKU ${skuId}, Vendor ${vendorLink.vendorId}:`, error);
      // Keep previousPrice as undefined and changes as 0
    }

    return {
      vendorId: vendorLink.vendorId,
      vendorName: vendor?.name || 'Unknown Vendor',
      currentPrice,
      previousPrice,
      priceChange,
      priceChangePercent,
      leadTime: vendorLink.leadTime || 0,
      minimumOrderQty: vendorLink.minimumOrderQty || 1,
      lastUpdated: vendorLink.lastUpdated || new Date().toISOString(),
      isPreferred: vendorLink.isPreferred || false,
      totalCost: currentPrice * quantity,
      priceHistory: priceHistory,
      performanceRating: vendor?.performanceRating || 0
    };
  }).sort((a, b) => a.totalCost - b.totalCost); // Sort by total cost
};

export const getPriceTrend = (skuId: number, vendorId: number): PriceTrend | null => {
  const sku = getSKUById(skuId);
  if (!sku) return null;

  const priceHistory = sku.priceHistory
    .filter(ph => ph.vendorId === vendorId)
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (priceHistory.length < 2) return null;

  const prices = priceHistory.map(ph => ph.price);
  const averagePrice = prices.reduce((sum, price) => sum + price, 0) / prices.length;
  const lowestPrice = Math.min(...prices);
  const highestPrice = Math.max(...prices);
  
  // Calculate trend
  const firstPrice = prices[0];
  const lastPrice = prices[prices.length - 1];
  const trendPercent = ((lastPrice - firstPrice) / firstPrice) * 100;
  
  let trend: 'increasing' | 'decreasing' | 'stable' = 'stable';
  if (Math.abs(trendPercent) > 5) {
    trend = trendPercent > 0 ? 'increasing' : 'decreasing';
  }

  // Calculate price volatility (standard deviation)
  const variance = prices.reduce((sum, price) => sum + Math.pow(price - averagePrice, 2), 0) / prices.length;
  const priceVolatility = Math.sqrt(variance);

  return {
    skuId,
    vendorId,
    trend,
    trendPercent,
    averagePrice,
    lowestPrice,
    highestPrice,
    priceVolatility
  };
};

export const getBestVendorForSKU = (skuId: number, quantity: number, priorityFactor: 'price' | 'leadTime' | 'performance' = 'price'): VendorPriceComparison | null => {
  const comparisons = getVendorPriceComparison(skuId, quantity);
  if (comparisons.length === 0) return null;

  // Filter vendors that can fulfill the minimum order quantity
  const eligibleVendors = comparisons.filter(comp => quantity >= comp.minimumOrderQty);
  if (eligibleVendors.length === 0) return comparisons[0]; // Return cheapest if none meet MOQ

  switch (priorityFactor) {
    case 'price':
      return eligibleVendors.sort((a, b) => a.totalCost - b.totalCost)[0];
    case 'leadTime':
      return eligibleVendors.sort((a, b) => a.leadTime - b.leadTime)[0];
    case 'performance':
      return eligibleVendors.sort((a, b) => b.performanceRating - a.performanceRating)[0];
    default:
      return eligibleVendors[0];
  }
};

export const getCostSavingsAnalysis = (skuId: number, quantity: number) => {
  const comparisons = getVendorPriceComparison(skuId, quantity);
  if (comparisons.length < 2) return null;

  const cheapest = comparisons[0];
  const mostExpensive = comparisons[comparisons.length - 1];
  const preferred = comparisons.find(c => c.isPreferred);

  const maxSavings = mostExpensive.totalCost - cheapest.totalCost;
  const maxSavingsPercent = (maxSavings / mostExpensive.totalCost) * 100;

  const preferredVsCheapest = preferred ? {
    savings: preferred.totalCost - cheapest.totalCost,
    savingsPercent: ((preferred.totalCost - cheapest.totalCost) / preferred.totalCost) * 100
  } : null;

  return {
    cheapestVendor: cheapest,
    mostExpensiveVendor: mostExpensive,
    preferredVendor: preferred,
    maxSavings,
    maxSavingsPercent,
    preferredVsCheapest,
    totalComparisons: comparisons.length
  };
};

export const getHistoricalPriceData = (skuId: number) => {
  const sku = getSKUById(skuId);
  if (!sku) return null;

  const priceData = sku.priceHistory.map(ph => {
    const vendor = getVendorById(ph.vendorId);
    return {
      ...ph,
      vendorName: vendor?.name || 'Unknown Vendor'
    };
  }).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  return {
    skuCode: sku.skuCode,
    skuName: sku.name,
    priceData,
    dateRange: {
      start: priceData[0]?.date,
      end: priceData[priceData.length - 1]?.date
    }
  };
};

// Utility function to validate price history entries
export const validatePriceHistoryEntry = (entry: PriceHistoryEntry): boolean => {
  return (
    entry &&
    typeof entry.id === 'number' &&
    typeof entry.vendorId === 'number' &&
    typeof entry.price === 'number' &&
    entry.price > 0 &&
    typeof entry.date === 'string' &&
    !isNaN(new Date(entry.date).getTime()) &&
    typeof entry.changedBy === 'number'
  );
};

// Utility function to clean and validate price history data
export const cleanPriceHistory = (priceHistory: PriceHistoryEntry[]): PriceHistoryEntry[] => {
  return priceHistory
    .filter(validatePriceHistoryEntry)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
};

// Utility function to format price change information
export const formatPriceChange = (priceChange: number, priceChangePercent: number, previousPrice?: number) => {
  if (previousPrice === undefined || previousPrice === 0) {
    return {
      text: 'No previous data',
      color: 'text-muted-foreground',
      icon: null,
      change: 0,
      percent: 0
    };
  }

  const isIncrease = priceChange > 0;
  const isDecrease = priceChange < 0;
  const isStable = Math.abs(priceChange) < 0.01; // Consider changes less than 1 cent as stable

  let color = 'text-muted-foreground';
  let icon = null;
  let text = '';

  if (isStable) {
    color = 'text-muted-foreground';
    text = 'No change';
  } else if (isIncrease) {
    color = 'text-red-600';
    text = `+${priceChange.toFixed(2)} (+${priceChangePercent.toFixed(1)}%)`;
  } else {
    color = 'text-green-600';
    text = `${priceChange.toFixed(2)} (${priceChangePercent.toFixed(1)}%)`;
  }

  return {
    text,
    color,
    icon,
    change: priceChange,
    percent: priceChangePercent
  };
};

// Generate additional mock data
export const generateMockSKUs = (count: number): SKU[] => {
  const categories = ['Electronics', 'Electrical', 'Power', 'Control', 'Automation'];
  const units = ['pieces', 'meters', 'rolls', 'sets', 'panels'];
  
  return Array.from({ length: count }, (_, index) => ({
    id: mockSKUs.length + index + 1,
    skuCode: `SKU-${String(mockSKUs.length + index + 1).padStart(3, '0')}`,
    name: `Generated Electrical Component ${index + 1}`,
    description: `Auto-generated electrical component for testing purposes`,
    category: categories[Math.floor(Math.random() * categories.length)],
    unit: units[Math.floor(Math.random() * units.length)],
    currentStock: Math.floor(Math.random() * 1000),
    reorderLevel: Math.floor(Math.random() * 100),
    maxStock: Math.floor(Math.random() * 2000) + 500,
    linkedVendors: [],
    priceHistory: [],
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    createdBy: 1
  }));
};

export default {
  mockUsers,
  mockVendors,
  mockSKUs,
  mockPurchaseOrders,
  mockQualityInspections,
  mockInvoices,
  mockGateEntries,
  getVendorById,
  getSKUById,
  getUserById,
  getPOById,
  generateMockSKUs,
  // Pricing analytics
  getVendorPriceComparison,
  getPriceTrend,
  getBestVendorForSKU,
  getCostSavingsAnalysis,
  getHistoricalPriceData,
  formatPriceChange,
  // Data validation
  validatePriceHistoryEntry,
  cleanPriceHistory
};

// Mock data for received invoices from vendors
const mockReceivedInvoices: Invoice[] = [
  {
    id: 1,
    invoiceNumber: 'INV-2025-001',
    poId: 1,
    poNumber: 'PO-2025-001',
    vendorId: 1,
    vendorName: 'Tech Solutions Ltd',
    poAmount: 50000, // Total PO amount
    amount: 25000, // Invoice amount (partial)
    subtotal: 22000,
    taxAmount: 3000,
    totalAmount: 25000,
    issueDate: '2025-01-20',
    invoiceDate: '2025-01-20',
    dueDate: '2025-02-19',
    status: 'Created',
    receivedStatus: 'Normal', // invoiced = received (50 = 50)
    paymentTerms: 'Net 30',
    // Quantity tracking
    poQuantity: 100, // Total quantity ordered in PO
    invoicedQuantity: 50, // Quantity invoiced in this invoice
    receivedQuantity: 50, // Total quantity received so far
    pendingQuantity: 50, // Quantity still to be received
    items: [
      {
        id: 1,
        skuId: 1,
        skuCode: 'SKU-001',
        skuName: 'Electrical Panel',
        quantity: 50,
        unitPrice: 500,
        totalPrice: 25000,
        receivedQuantity: 50,
        pendingQuantity: 0
      }
    ],
    notes: 'First partial invoice for PO-2025-001 - 50 units received',
    attachments: ['invoice_001.pdf'],
    createdAt: '2025-01-22T08:30:00Z',
    updatedAt: '2025-01-22T08:30:00Z'
  },
  {
    id: 2,
    invoiceNumber: 'INV-2025-002',
    poId: 2,
    poNumber: 'PO-2025-002',
    vendorId: 2,
    vendorName: 'Power Systems Solutions',
    poAmount: 45000, // Total PO amount
    amount: 45000, // Invoice amount (full)
    subtotal: 40000,
    taxAmount: 5000,
    totalAmount: 45000,
    issueDate: '2025-01-25',
    invoiceDate: '2025-01-25',
    dueDate: '2025-02-24',
    status: 'Approved',
    receivedStatus: 'Normal', // 100% of PO quantity received - invoiced = received
    paymentTerms: 'Net 30',
    paymentMethod: 'Bank Transfer',
    paymentDate: '2025-01-28',
    // Quantity tracking
    poQuantity: 75, // Total quantity ordered in PO
    invoicedQuantity: 75, // Quantity invoiced in this invoice
    receivedQuantity: 75, // Total quantity received so far
    pendingQuantity: 0, // Quantity still to be received
    items: [
      {
        id: 2,
        skuId: 2,
        skuCode: 'SKU-002',
        skuName: 'Control Board',
        quantity: 75,
        unitPrice: 600,
        totalPrice: 45000,
        receivedQuantity: 75,
        pendingQuantity: 0
      }
    ],
    notes: 'Complete invoice for PO-2025-002 - 75 units received',
    attachments: ['invoice_002.pdf', 'delivery_receipt.pdf'],
    createdAt: '2025-01-26T10:15:00Z',
    updatedAt: '2025-01-28T16:45:00Z'
  },
  {
    id: 3,
    invoiceNumber: 'INV-2025-003',
    poId: 1,
    poNumber: 'PO-2025-001',
    vendorId: 1,
    vendorName: 'Tech Solutions Ltd',
    poAmount: 50000, // Total PO amount
    amount: 15000, // Invoice amount (remaining)
    subtotal: 13000,
    taxAmount: 2000,
    totalAmount: 15000,
    issueDate: '2025-01-30',
    invoiceDate: '2025-01-30',
    dueDate: '2025-03-01',
    status: 'Approved',
    receivedStatus: 'Normal', // invoiced = received (30 = 30)
    paymentTerms: 'Net 30',
    // Quantity tracking
    poQuantity: 100, // Total quantity ordered in PO
    invoicedQuantity: 30, // Quantity invoiced in this invoice
    receivedQuantity: 80, // Total quantity received so far (50+30)
    pendingQuantity: 20, // Quantity still to be received
    items: [
      {
        id: 3,
        skuId: 1,
        skuCode: 'SKU-001',
        skuName: 'Electrical Panel',
        quantity: 30,
        unitPrice: 500,
        totalPrice: 15000,
        receivedQuantity: 30,
        pendingQuantity: 0
      }
    ],
    notes: 'Second partial invoice for PO-2025-001 - 30 additional units received',
    attachments: ['invoice_003.pdf'],
    createdAt: '2025-02-01T11:45:00Z',
    updatedAt: '2025-02-02T09:30:00Z'
  },
  {
    id: 4,
    invoiceNumber: 'INV-2025-004',
    poId: 4,
    poNumber: 'PO-2025-004',
    vendorId: 3,
    vendorName: 'Industrial Electronics Supply',
    poAmount: 40000, // Total PO amount
    amount: 32000, // Invoice amount (partial)
    subtotal: 28000,
    taxAmount: 4000,
    totalAmount: 32000,
    issueDate: '2025-02-05',
    invoiceDate: '2025-02-05',
    dueDate: '2025-03-07',
    status: 'Pending',
    receivedStatus: 'Normal', // invoiced = received (64 = 64)
    paymentTerms: 'Net 30',
    // Quantity tracking
    poQuantity: 80, // Total quantity ordered in PO
    invoicedQuantity: 64, // Quantity invoiced in this invoice
    receivedQuantity: 64, // Total quantity received so far
    pendingQuantity: 16, // Quantity still to be received
    items: [
      {
        id: 4,
        skuId: 4,
        skuCode: 'SKU-004',
        skuName: 'Power Cable',
        quantity: 64,
        unitPrice: 500,
        totalPrice: 32000,
        receivedQuantity: 64,
        pendingQuantity: 0
      }
    ],
    notes: 'Invoice received - 64 units received, 16 pending',
    attachments: ['invoice_004.pdf'],
    createdAt: '2025-02-06T13:20:00Z',
    updatedAt: '2025-02-06T13:20:00Z'
  },
  {
    id: 5,
    invoiceNumber: 'INV-2025-005',
    poId: 5,
    poNumber: 'PO-2025-005',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 25000, // Total PO amount
    amount: 25000, // Invoice amount (full)
    subtotal: 22000,
    taxAmount: 3000,
    totalAmount: 25000,
    issueDate: '2025-02-10',
    invoiceDate: '2025-02-10',
    dueDate: '2025-03-12',
    status: 'Approved',
    receivedStatus: 'Normal', // invoiced = received (100 = 100)
    paymentTerms: 'Net 30',
    paymentMethod: 'Bank Transfer',
    paymentDate: '2025-02-15',
    poQuantity: 100, // Total quantity ordered in PO
    invoicedQuantity: 100, // Quantity invoiced in this invoice
    receivedQuantity: 100, // Total quantity received so far
    pendingQuantity: 0, // Quantity still to be received
    items: [
      {
        id: 5,
        skuId: 1,
        skuCode: 'SKU-001',
        skuName: 'LED Bulbs 9W',
        quantity: 100,
        unitPrice: 250,
        totalPrice: 25000,
        receivedQuantity: 100,
        pendingQuantity: 0
      }
    ],
    notes: 'Complete invoice for PO-2025-005 - 100 units received',
    attachments: ['invoice_005.pdf'],
    createdAt: '2025-02-10T09:30:00Z',
    updatedAt: '2025-02-15T14:20:00Z'
  },
  {
    id: 6,
    invoiceNumber: 'INV-2025-006',
    poId: 6,
    poNumber: 'PO-2025-006',
    vendorId: 2,
    vendorName: 'Power Systems Solutions',
    poAmount: 60000, // Total PO amount
    amount: 30000, // Invoice amount (partial)
    subtotal: 27000,
    taxAmount: 3000,
    totalAmount: 30000,
    issueDate: '2025-02-12',
    invoiceDate: '2025-02-12',
    dueDate: '2025-03-14',
    status: 'Pending',
    receivedStatus: 'Not Received', // invoiced < received (50 < 60)
    paymentTerms: 'Net 30',
    poQuantity: 100, // Total quantity ordered in PO
    invoicedQuantity: 50, // Quantity invoiced in this invoice
    receivedQuantity: 60, // Total quantity received so far (more than invoiced)
    pendingQuantity: 40, // Quantity still to be received
    items: [
      {
        id: 6,
        skuId: 2,
        skuCode: 'SKU-002',
        skuName: 'Capacitor 100uF',
        quantity: 50,
        unitPrice: 600,
        totalPrice: 30000,
        receivedQuantity: 60,
        pendingQuantity: 0
      }
    ],
    notes: 'Partial invoice for PO-2025-006 - 50 units invoiced, 60 units actually received',
    attachments: ['invoice_006.pdf'],
    createdAt: '2025-02-12T11:15:00Z',
    updatedAt: '2025-02-12T11:15:00Z'
  },
  {
    id: 7,
    invoiceNumber: 'INV-2025-007',
    poId: 7,
    poNumber: 'PO-2025-007',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 35000, // Total PO amount
    amount: 17500, // Invoice amount (partial)
    subtotal: 15000,
    taxAmount: 2500,
    totalAmount: 17500,
    issueDate: '2025-02-14',
    invoiceDate: '2025-02-14',
    dueDate: '2025-03-16',
    status: 'Approved',
    receivedStatus: 'Not Received', // invoiced > received (70 > 50)
    paymentTerms: 'Net 30',
    poQuantity: 100, // Total quantity ordered in PO
    invoicedQuantity: 70, // Quantity invoiced in this invoice
    receivedQuantity: 50, // Total quantity received so far (less than invoiced)
    pendingQuantity: 50, // Quantity still to be received
    items: [
      {
        id: 7,
        skuId: 3,
        skuCode: 'SKU-003',
        skuName: 'LED 5mm Red',
        quantity: 70,
        unitPrice: 250,
        totalPrice: 17500,
        receivedQuantity: 50,
        pendingQuantity: 20
      }
    ],
    notes: 'Partial invoice for PO-2025-007 - 70 units invoiced, only 50 units received',
    attachments: ['invoice_007.pdf'],
    createdAt: '2025-02-14T13:45:00Z',
    updatedAt: '2025-02-14T13:45:00Z'
  },
  {
    id: 8,
    invoiceNumber: 'INV-2025-008',
    poId: 10,
    poNumber: 'PO-2025-012',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 40000, // Total PO amount
    amount: 40000, // Invoice amount (full)
    subtotal: 36000,
    taxAmount: 4000,
    totalAmount: 40000,
    issueDate: '2025-02-16',
    invoiceDate: '2025-02-16',
    dueDate: '2025-03-18',
    status: 'Approved',
    receivedStatus: 'Normal', // invoiced = received (80 = 80)
    paymentTerms: 'Net 30',
    paymentMethod: 'Bank Transfer',
    paymentDate: '2025-02-20',
    poQuantity: 80, // Total quantity ordered in PO
    invoicedQuantity: 80, // Quantity invoiced in this invoice
    receivedQuantity: 80, // Total quantity received so far
    pendingQuantity: 0, // Quantity still to be received
    items: [
      {
        id: 8,
        skuId: 4,
        skuCode: 'SKU-004',
        skuName: 'Power Cable',
        quantity: 80,
        unitPrice: 500,
        totalPrice: 40000,
        receivedQuantity: 80,
        pendingQuantity: 0
      }
    ],
    notes: 'Complete invoice for PO-2025-008 - 80 units received',
    attachments: ['invoice_008.pdf'],
    createdAt: '2025-02-16T10:20:00Z',
    updatedAt: '2025-02-20T16:30:00Z'
  },
  // Additional invoices for quality testing showcase
  {
    id: 9,
    invoiceNumber: 'INV-2025-009',
    poId: 1,
    poNumber: 'PO-2025-001',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 2500,
    amount: 2500,
    subtotal: 2250,
    taxAmount: 250,
    totalAmount: 2500,
    issueDate: '2025-02-25',
    invoiceDate: '2025-02-25',
    dueDate: '2025-03-27',
    status: 'Approved',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 100,
    pendingQuantity: 0,
    items: [
      {
        id: 9,
        skuId: 1,
        skuCode: 'SKU-001',
        skuName: 'Resistor 10K Ohm 1/4W',
        quantity: 50,
        unitPrice: 0.50,
        totalPrice: 25.00,
        description: 'High-quality carbon film resistor',
        receivedQuantity: 50,
        pendingQuantity: 0
      },
      {
        id: 10,
        skuId: 2,
        skuCode: 'SKU-002',
        skuName: 'Capacitor 100µF 16V',
        quantity: 50,
        unitPrice: 0.75,
        totalPrice: 37.50,
        description: 'Electrolytic capacitor',
        receivedQuantity: 50,
        pendingQuantity: 0
      }
    ],
    notes: 'Ready for quality testing - all items received and security verified',
    attachments: ['invoice_009.pdf'],
    createdAt: '2025-02-25T14:30:00Z',
    updatedAt: '2025-02-25T14:30:00Z'
  },
  {
    id: 10,
    invoiceNumber: 'INV-2025-010',
    poId: 2,
    poNumber: 'PO-2025-002',
    vendorId: 2,
    vendorName: 'PowerTech Solutions',
    poAmount: 1500,
    amount: 1500,
    subtotal: 1350,
    taxAmount: 150,
    totalAmount: 1500,
    issueDate: '2025-02-26',
    invoiceDate: '2025-02-26',
    dueDate: '2025-03-28',
    status: 'Approved',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 25,
    invoicedQuantity: 25,
    receivedQuantity: 25,
    pendingQuantity: 0,
    items: [
      {
        id: 11,
        skuId: 3,
        skuCode: 'SKU-003',
        skuName: 'LED Red 5mm',
        quantity: 25,
        unitPrice: 0.25,
        totalPrice: 6.25,
        description: 'Standard red LED',
        receivedQuantity: 25,
        pendingQuantity: 0
      }
    ],
    notes: 'Ready for quality testing - LED components received',
    attachments: ['invoice_010.pdf'],
    createdAt: '2025-02-26T10:15:00Z',
    updatedAt: '2025-02-26T10:15:00Z'
  },
  // Additional invoices for Security Verification showcase
  {
    id: 11,
    invoiceNumber: 'INV-2025-011',
    poId: 3,
    poNumber: 'PO-2025-003',
    vendorId: 3,
    vendorName: 'TechSupplies Inc',
    poAmount: 2000,
    amount: 2000,
    subtotal: 1800,
    taxAmount: 200,
    totalAmount: 2000,
    issueDate: '2025-02-27',
    invoiceDate: '2025-02-27',
    dueDate: '2025-03-29',
    status: 'Pending',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 40,
    invoicedQuantity: 40,
    receivedQuantity: 40,
    pendingQuantity: 0,
    items: [
      {
        id: 12,
        skuId: 4,
        skuCode: 'SKU-004',
        skuName: 'Fuse 5A 250V',
        quantity: 20,
        unitPrice: 2.50,
        totalPrice: 50.00,
        description: 'Glass fuse 5A 250V',
        receivedQuantity: 20,
        pendingQuantity: 0
      },
      {
        id: 13,
        skuId: 5,
        skuCode: 'SKU-005',
        skuName: 'Switch Toggle SPST',
        quantity: 20,
        unitPrice: 3.00,
        totalPrice: 60.00,
        description: 'Toggle switch single pole single throw',
        receivedQuantity: 20,
        pendingQuantity: 0
      }
    ],
    notes: 'Awaiting security verification - all items received',
    attachments: ['invoice_011.pdf'],
    createdAt: '2025-02-27T09:30:00Z',
    updatedAt: '2025-02-27T09:30:00Z'
  },
  {
    id: 12,
    invoiceNumber: 'INV-2025-012',
    poId: 4,
    poNumber: 'PO-2025-004',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 1200,
    amount: 1200,
    subtotal: 1080,
    taxAmount: 120,
    totalAmount: 1200,
    issueDate: '2025-02-28',
    invoiceDate: '2025-02-28',
    dueDate: '2025-03-30',
    status: 'Pending',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 60,
    invoicedQuantity: 60,
    receivedQuantity: 60,
    pendingQuantity: 0,
    items: [
      {
        id: 14,
        skuId: 6,
        skuCode: 'SKU-006',
        skuName: 'Connector RJ45',
        quantity: 60,
        unitPrice: 1.50,
        totalPrice: 90.00,
        description: 'Ethernet connector RJ45',
        receivedQuantity: 60,
        pendingQuantity: 0
      }
    ],
    notes: 'Network components ready for security verification',
    attachments: ['invoice_012.pdf'],
    createdAt: '2025-02-28T11:45:00Z',
    updatedAt: '2025-02-28T11:45:00Z'
  },
  {
    id: 13,
    invoiceNumber: 'INV-2025-013',
    poId: 5,
    poNumber: 'PO-2025-005',
    vendorId: 2,
    vendorName: 'PowerTech Solutions',
    poAmount: 800,
    amount: 800,
    subtotal: 720,
    taxAmount: 80,
    totalAmount: 800,
    issueDate: '2025-03-01',
    invoiceDate: '2025-03-01',
    dueDate: '2025-03-31',
    status: 'Created',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 15,
    invoicedQuantity: 15,
    receivedQuantity: 15,
    pendingQuantity: 0,
    items: [
      {
        id: 15,
        skuId: 7,
        skuCode: 'SKU-007',
        skuName: 'Capacitor 1000µF 25V',
        quantity: 15,
        unitPrice: 2.25,
        totalPrice: 33.75,
        description: 'High-quality electrolytic capacitor',
        receivedQuantity: 15,
        pendingQuantity: 0
      }
    ],
    notes: 'Security verified - ready for IQC testing',
    attachments: ['invoice_013.pdf'],
    createdAt: '2025-03-01T14:20:00Z',
    updatedAt: '2025-03-01T14:20:00Z'
  },
  // Additional invoices specifically for Quality Control testing
  {
    id: 14,
    invoiceNumber: 'INV-2025-014',
    poId: 6,
    poNumber: 'PO-2025-006',
    vendorId: 1,
    vendorName: 'Electronic Components Co',
    poAmount: 3000,
    amount: 3000,
    subtotal: 2700,
    taxAmount: 300,
    totalAmount: 3000,
    issueDate: '2025-03-02',
    invoiceDate: '2025-03-02',
    dueDate: '2025-04-01',
    status: 'Approved',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 100,
    invoicedQuantity: 100,
    receivedQuantity: 100,
    pendingQuantity: 0,
    items: [
      {
        id: 16,
        skuId: 1,
        skuCode: 'SKU-001',
        skuName: 'Resistor 10K Ohm 1/4W',
        quantity: 50,
        unitPrice: 0.50,
        totalPrice: 25.00,
        description: 'High-quality carbon film resistor',
        receivedQuantity: 50,
        pendingQuantity: 0
      },
      {
        id: 17,
        skuId: 2,
        skuCode: 'SKU-002',
        skuName: 'Capacitor 100µF 16V',
        quantity: 50,
        unitPrice: 0.75,
        totalPrice: 37.50,
        description: 'Electrolytic capacitor',
        receivedQuantity: 50,
        pendingQuantity: 0
      }
    ],
    notes: 'Ready for quality testing - electronic components received and security verified',
    attachments: ['invoice_014.pdf'],
    createdAt: '2025-03-02T10:15:00Z',
    updatedAt: '2025-03-02T10:15:00Z'
  },
  {
    id: 15,
    invoiceNumber: 'INV-2025-015',
    poId: 7,
    poNumber: 'PO-2025-007',
    vendorId: 2,
    vendorName: 'PowerTech Solutions',
    poAmount: 1800,
    amount: 1800,
    subtotal: 1620,
    taxAmount: 180,
    totalAmount: 1800,
    issueDate: '2025-03-03',
    invoiceDate: '2025-03-03',
    dueDate: '2025-04-02',
    status: 'Approved',
    receivedStatus: 'Received',
    paymentStatus: 'Unpaid',
    paymentTerms: 'Net 30',
    poQuantity: 30,
    invoicedQuantity: 30,
    receivedQuantity: 30,
    pendingQuantity: 0,
    items: [
      {
        id: 18,
        skuId: 3,
        skuCode: 'SKU-003',
        skuName: 'LED Red 5mm',
        quantity: 30,
        unitPrice: 0.25,
        totalPrice: 7.50,
        description: 'Standard red LED',
        receivedQuantity: 30,
        pendingQuantity: 0
      }
    ],
    notes: 'LED components ready for quality testing',
    attachments: ['invoice_015.pdf'],
    createdAt: '2025-03-03T14:30:00Z',
    updatedAt: '2025-03-03T14:30:00Z'
  }
];

// Mock storage locations
export const mockStorageLocations = [
  'Assembly Line A',
  'Assembly Line B', 
  'Storage Room 1',
  'Storage Room 2',
  'Quality Control Area',
  'Packaging Department',
  'Main Warehouse',
  'Cold Storage',
  'Electronics Storage',
  'Raw Materials Storage'
];

// Mock handover logs
export const mockHandoverLogs: HandoverLog[] = [
  {
    id: 1,
    handoverId: 'HOV-2025-001',
    invoiceId: 14,
    invoiceNumber: 'INV-2025-014',
    floorManagerName: 'Rajesh Kumar',
    floorManagerContact: '+91-9876543210',
    handoverDateTime: '2025-03-10T10:30:00Z',
    storageLocation: 'Assembly Line A',
    status: 'Handed Over',
    createdAt: '2025-03-10T10:30:00Z',
    updatedAt: '2025-03-10T10:30:00Z'
  },
  {
    id: 2,
    handoverId: 'HOV-2025-002',
    invoiceId: 15,
    invoiceNumber: 'INV-2025-015',
    floorManagerName: 'Priya Sharma',
    floorManagerContact: '+91-9876543211',
    handoverDateTime: '2025-03-10T11:15:00Z',
    storageLocation: 'Storage Room 1',
    status: 'Handed Over',
    createdAt: '2025-03-10T11:15:00Z',
    updatedAt: '2025-03-10T11:15:00Z'
  },
  {
    id: 3,
    handoverId: 'HOV-2025-003',
    invoiceId: 8,
    invoiceNumber: 'INV-2025-008',
    floorManagerName: 'John Smith',
    floorManagerContact: '+91-9876543212',
    handoverDateTime: '2025-01-31T11:00:00Z',
    storageLocation: 'Warehouse B',
    status: 'Handed Over',
    createdAt: '2025-01-31T11:00:00Z',
    updatedAt: '2025-01-31T11:00:00Z'
  },
  {
    id: 4,
    handoverId: 'HOV-2025-004',
    invoiceId: 9,
    invoiceNumber: 'INV-2025-009',
    floorManagerName: 'Sarah Johnson',
    floorManagerContact: '+91-9876543213',
    handoverDateTime: '2025-01-31T12:00:00Z',
    storageLocation: 'Quality Control Area',
    status: 'Handed Over',
    createdAt: '2025-01-31T12:00:00Z',
    updatedAt: '2025-01-31T12:00:00Z'
  },
  {
    id: 5,
    handoverId: 'HOV-2025-005',
    invoiceId: 10,
    invoiceNumber: 'INV-2025-010',
    floorManagerName: 'Mike Wilson',
    floorManagerContact: '+91-9876543214',
    handoverDateTime: '2025-01-30T15:00:00Z',
    storageLocation: 'Main Warehouse',
    status: 'Completed',
    createdAt: '2025-01-30T15:00:00Z',
    updatedAt: '2025-01-31T16:00:00Z'
  },
  {
    id: 6,
    handoverId: 'HOV-2025-006',
    invoiceId: 1,
    invoiceNumber: 'INV-2025-001',
    floorManagerName: 'Alex Brown',
    floorManagerContact: '+91-9876543215',
    handoverDateTime: '2025-01-25T14:30:00Z',
    storageLocation: 'Production Floor',
    status: 'Completed',
    createdAt: '2025-01-25T14:30:00Z',
    updatedAt: '2025-01-26T10:00:00Z'
  },
  {
    id: 7,
    handoverId: 'HOV-2025-007',
    invoiceId: 2,
    invoiceNumber: 'INV-2025-002',
    floorManagerName: 'Lisa Davis',
    floorManagerContact: '+91-9876543216',
    handoverDateTime: '2025-01-26T09:45:00Z',
    storageLocation: 'Assembly Line B',
    status: 'Completed',
    createdAt: '2025-01-26T09:45:00Z',
    updatedAt: '2025-01-27T11:30:00Z'
  },
  {
    id: 8,
    handoverId: 'HOV-2025-008',
    invoiceId: 11,
    invoiceNumber: 'INV-2025-011',
    floorManagerName: 'John Smith',
    floorManagerContact: '+91-9876543217',
    handoverDateTime: '2025-01-31T08:30:00Z',
    storageLocation: 'Quality Control Area',
    status: 'Handed Over',
    createdAt: '2025-01-31T08:30:00Z',
    updatedAt: '2025-01-31T08:30:00Z'
  },
  {
    id: 9,
    handoverId: 'HOV-2025-009',
    invoiceId: 12,
    invoiceNumber: 'INV-2025-012',
    floorManagerName: 'Sarah Johnson',
    floorManagerContact: '+91-9876543218',
    handoverDateTime: '2025-01-31T09:45:00Z',
    storageLocation: 'Warehouse A',
    status: 'Handed Over',
    createdAt: '2025-01-31T09:45:00Z',
    updatedAt: '2025-01-31T09:45:00Z'
  },
  {
    id: 10,
    handoverId: 'HOV-2025-010',
    invoiceId: 13,
    invoiceNumber: 'INV-2025-013',
    floorManagerName: 'Sarah Johnson',
    floorManagerContact: '+91-9876543219',
    handoverDateTime: '2025-01-31T12:30:00Z',
    storageLocation: 'Lighting Section',
    status: 'Handed Over',
    createdAt: '2025-01-31T12:30:00Z',
    updatedAt: '2025-01-31T12:30:00Z'
  },
  {
    id: 11,
    handoverId: 'HOV-2025-011',
    invoiceId: 14,
    invoiceNumber: 'INV-2025-014',
    floorManagerName: 'John Smith',
    floorManagerContact: '+91-9876543220',
    handoverDateTime: '2025-01-31T13:00:00Z',
    storageLocation: 'Automation Lab',
    status: 'Handed Over',
    createdAt: '2025-01-31T13:00:00Z',
    updatedAt: '2025-01-31T13:00:00Z'
  },
  {
    id: 12,
    handoverId: 'HOV-2025-012',
    invoiceId: 15,
    invoiceNumber: 'INV-2025-015',
    floorManagerName: 'Mike Wilson',
    floorManagerContact: '+91-9876543221',
    handoverDateTime: '2025-01-30T16:00:00Z',
    storageLocation: 'Electrical Workshop',
    status: 'Completed',
    createdAt: '2025-01-30T16:00:00Z',
    updatedAt: '2025-01-31T15:30:00Z'
  },
  {
    id: 13,
    handoverId: 'HOV-2025-013',
    invoiceId: 16,
    invoiceNumber: 'INV-2025-016',
    floorManagerName: 'Lisa Davis',
    floorManagerContact: '+91-9876543222',
    handoverDateTime: '2025-01-29T15:30:00Z',
    storageLocation: 'Smart Home Section',
    status: 'Completed',
    createdAt: '2025-01-29T15:30:00Z',
    updatedAt: '2025-01-31T14:15:00Z'
  }
];

// Additional comprehensive mock data removed to avoid type conflicts
// Export all mock data - Updated to fix cache issues
export {
  mockUsers,
  mockVendors,
  mockSKUs,
  mockPurchaseOrders,
  mockQualityInspections,
  mockInvoices,
  mockReceivedInvoices,
  mockGateEntries,
  mockSentPOsToVendors
};
